import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Play, ArrowLeft, Crown, Clock, Eye, Share2, 
  Check, CheckCircle, Lock, ShoppingCart, CreditCard, Film, Pause, 
  Volume2, VolumeX, Maximize, Minimize, X, Bookmark, BookmarkCheck,
  Star, Send, Trash2, Loader2, Bell, BellOff, Sparkles, Settings, Cast, Airplay,
  ChevronDown, ChevronUp, Info, Copy, Link as LinkIcon, MessageCircle, Smile,
  ThumbsUp, ThumbsDown, LogIn, User, Users, PictureInPicture2, BadgeCheck, Tag
} from 'lucide-react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import VODFooter from '../components/VODFooter';
import VODPlayer from '../components/VODPlayer';
import CloudflareStreamPlayer from '../components/CloudflareStreamPlayer';

const API = process.env.REACT_APP_BACKEND_URL || '';
const SITE_URL = 'https://ltvproduction.com';
const API_SHARE_URL = `${SITE_URL}/api/vod/share`;
const CLOUDFLARE_CUSTOMER_CODE = 'xg4xjbetrvqn62vp';

// Helper to detect if user is on ltvvod.com forwarding domain
const isOnLtvvodDomain = () => {
  if (typeof window !== 'undefined') {
    return window.location.hostname.includes('ltvvod.com');
  }
  return false;
};

// Get login URL with proper redirect - adds from=vod or from=ltvvod based on domain
const getLoginUrl = (redirectPath) => {
  const fromParam = isOnLtvvodDomain() ? 'from=ltvvod&' : 'from=vod&';
  return `/live/login?${fromParam}redirect=${encodeURIComponent(redirectPath)}`;
};

const getSignupUrl = (redirectPath) => {
  const fromParam = isOnLtvvodDomain() ? 'from=ltvvod&' : 'from=vod&';
  return `/live/login?${fromParam}tab=signup&redirect=${encodeURIComponent(redirectPath)}`;
};

// VAST tag URL - Using Google's SKIPPABLE test tag in HD (skip after 5 seconds)
// TODO: Replace with your own VAST tag once Google Ad Manager is properly configured
// VAST tag is now fetched dynamically from ad settings via useEnhancedVideoAds hook

// Mobile Ad Skip Button Component - for Google ads on mobile using custom playback
const MobileAdSkipButton = ({ videoRef, onSkip }) => {
  const [canSkip, setCanSkip] = useState(false);
  const [countdown, setCountdown] = useState(5);
  
  useEffect(() => {
    const video = videoRef?.current;
    if (!video) return;
    
    const skipTime = video._skipOffset || 5;
    
    const checkSkipTime = () => {
      const currentTime = video.currentTime || 0;
      const remaining = Math.max(0, Math.ceil(skipTime - currentTime));
      setCountdown(remaining);
      
      if (currentTime >= skipTime) {
        setCanSkip(true);
      }
    };
    
    // Check immediately and on timeupdate
    checkSkipTime();
    video.addEventListener('timeupdate', checkSkipTime);
    
    return () => {
      video.removeEventListener('timeupdate', checkSkipTime);
    };
  }, [videoRef]);
  
  return (
    <div className="absolute bottom-16 right-3" style={{ zIndex: 10010 }} data-testid="mobile-ad-skip-container">
      {canSkip ? (
        <button
          onClick={(e) => { 
            e.stopPropagation(); 
            onSkip();
          }}
          className="bg-white/90 text-black px-3 py-1.5 rounded text-sm font-semibold hover:bg-white transition-colors flex items-center gap-1"
          data-testid="mobile-ad-skip-button"
        >
          Skip Ad →
        </button>
      ) : (
        <div className="bg-black/80 text-white px-3 py-1.5 rounded-lg text-sm" data-testid="mobile-ad-skip-countdown">
          Skip in {countdown}s
        </div>
      )}
    </div>
  );
};

const VODVideoDetail = () => {
  const navigate = useNavigate();
  const { videoId, slug } = useParams(); // Support both videoId and slug URLs
  const [searchParams] = useSearchParams();
  const isSharedLink = searchParams.get('shared') === 'true';
  const shouldAutoPlay = searchParams.get('autoplay') === 'true';
  const resumeTimestamp = searchParams.get('t') ? parseFloat(searchParams.get('t')) : null;
  
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [relatedVideos, setRelatedVideos] = useState([]);
  
  // Premiere state
  const [hasReminder, setHasReminder] = useState(false);
  const [reminderCount, setReminderCount] = useState(0);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [premiereCountdown, setPremiereCountdown] = useState(0);
  
  // Helper function to check if video is premiering (future premiere date)
  const isVideoPremiere = (videoData) => {
    if (!videoData) return false;
    
    // Check is_premiering_soon flag from API
    if (videoData.is_premiering_soon === true) return true;
    
    // Fallback: Check visibility_status and premiere_date directly
    if (videoData.visibility_status === 'premiere' && videoData.premiere_date) {
      const premiereDate = new Date(videoData.premiere_date);
      const now = new Date();
      return premiereDate > now;
    }
    
    return false;
  };
  
  // Calculate countdown seconds from premiere_date
  const getPremiereCountdownSeconds = (videoData) => {
    if (!videoData?.premiere_date) return 0;
    const premiereDate = new Date(videoData.premiere_date);
    const now = new Date();
    return Math.max(0, Math.floor((premiereDate - now) / 1000));
  };
  
  // Watchlist state
  const [inWatchlist, setInWatchlist] = useState(false);
  const [watchlistLoading, setWatchlistLoading] = useState(false);
  
  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ average_rating: 0, review_count: 0 });
  const [userRating, setUserRating] = useState(0);
  const [userReviewText, setUserReviewText] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [userHasReviewed, setUserHasReviewed] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  
  // Quick comment popup state (for video player)
  const [showQuickComment, setShowQuickComment] = useState(false);
  const [quickCommentText, setQuickCommentText] = useState('');
  const [quickCommentSubmitting, setQuickCommentSubmitting] = useState(false);
  const [quickCommentSuccess, setQuickCommentSuccess] = useState(false);
  const [inlineCommentTimestamp, setInlineCommentTimestamp] = useState(null); // Timestamp when comment was started
  
  // Mobile full-screen comment modal state
  const [showMobileCommentModal, setShowMobileCommentModal] = useState(false);
  const [mobileCommentTimestamp, setMobileCommentTimestamp] = useState(0);
  const [videoWasPlaying, setVideoWasPlaying] = useState(false);
  const [showMobileEmojiPicker, setShowMobileEmojiPicker] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Trailer player state
  const [showTrailer, setShowTrailer] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const trailerRef = useRef(null);
  const hlsRef = useRef(null);
  
  // Video player state (inline player - replaces thumbnail)
  const [showVideoModal, setShowVideoModal] = useState(false); // Keep for backwards compat
  const [playingInline, setPlayingInline] = useState(false); // NEW: inline player mode
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoMuted, setVideoMuted] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const videoPlayerRef = useRef(null);
  const videoHlsRef = useRef(null);
  const inlineAdContainerRef = useRef(null); // Ad container for inline player
  const watchTrackingRef = useRef({ lastTracked: 0, totalWatched: 0, viewCounted: false }); // Track watch time for analytics
  const heartbeatIntervalRef = useRef(null); // Real-time heartbeat interval
  const viewerSessionId = useRef(`session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`); // Unique session ID
  
  // Quality control state for inline player
  const [modalCurrentQuality, setModalCurrentQuality] = useState('4k');
  const [showQualityMenu, setShowQualityMenu] = useState(false);
  const [autoQualityEnabled, setAutoQualityEnabled] = useState(true);
  const [qualityDropped, setQualityDropped] = useState(false);
  const [showQualityNotification, setShowQualityNotification] = useState(false);
  const [bufferingCount, setBufferingCount] = useState(0);
  const lastBufferCheckRef = useRef(Date.now());
  
  // DEBUG: Ad debug overlay state (remove after debugging)
  const [adDebugInfo, setAdDebugInfo] = useState({ time: 0, nextAd: '', status: 'Loading...' });
  const [showAdDebug, setShowAdDebug] = useState(true); // Set to false to disable debug overlay
  const [adErrorMessage, setAdErrorMessage] = useState(null); // Track ad errors
  
  // Ad state for inline player
  const [inlineAdPlaying, setInlineAdPlaying] = useState(false);
  const [adRequestPending, setAdRequestPending] = useState(false); // Track when ad is loading
  const preRollAttemptedRef = useRef(false);
  const preRollCompletedRef = useRef(false); // CRITICAL: Tracks if pre-roll was played/skipped - NEVER reset during same video
  const isRestoringContentRef = useRef(false); // CRITICAL: Flag to prevent ad re-trigger during content restoration
  const pendingSeekTimestampRef = useRef(null); // Store timestamp to seek to after ad finishes
  
  // Picture-in-Picture state
  const [isPiPActive, setIsPiPActive] = useState(false);
  
  // Backwards compatibility alias for old modal code
  const modalAdPlaying = inlineAdPlaying;
  const modalAdContainerRef = inlineAdContainerRef;
  
  // Signup prompt state for non-logged in users
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);
  const [signupPromptShown, setSignupPromptShown] = useState(false);
  const [popupSettingsLoaded, setPopupSettingsLoaded] = useState(false); // Track if settings are loaded
  const [popupSettings, setPopupSettings] = useState({
    enabled: false, // Default to disabled until settings are loaded from backend
    trigger_type: 'percentage',
    trigger_value: 50,
    title: 'Enjoying the content?',
    message: 'Create a FREE account to stay updated with:',
    benefits: ['New episode releases', 'Exclusive series & documentaries', 'Live concerts & shows', 'Save your watch progress'],
    cta_button_text: 'Create Free Account',
    dismiss_button_text: 'Maybe later'
  });
  
  // Description expand state
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  
  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  
  // Continue watching state (for logged-in users)
  const [showResumePrompt, setShowResumePrompt] = useState(false);
  const [savedProgress, setSavedProgress] = useState(null); // { progress: seconds, completion_rate: % }
  const [watchProgressLoaded, setWatchProgressLoaded] = useState(false); // true once progress check completes
  const [skipPreRollOnResume, setSkipPreRollOnResume] = useState(false); // Skip pre-roll ad when resuming
  
  // Like/Dislike state
  const [userReaction, setUserReaction] = useState(null); // 'like', 'dislike', or null
  const [likeCount, setLikeCount] = useState(0);
  const [dislikeCount, setDislikeCount] = useState(0);
  const [reactionLoading, setReactionLoading] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  
  // Inline auth state for paywall
  const [paywallAuthMode, setPaywallAuthMode] = useState('login'); // 'login' or 'register'
  const [paywallEmail, setPaywallEmail] = useState('');
  const [paywallPassword, setPaywallPassword] = useState('');
  const [paywallName, setPaywallName] = useState('');
  const [paywallAuthLoading, setPaywallAuthLoading] = useState(false);
  const [paywallAuthError, setPaywallAuthError] = useState('');
  const [pendingPurchaseAfterAuth, setPendingPurchaseAfterAuth] = useState(false);
  
  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [promoApplied, setPromoApplied] = useState(null); // { code, discount_percentage, discounted_price, is_free }
  
  // Username modal state (for commenting)
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [savingUsername, setSavingUsername] = useState(false);
  
  // Reply state (for threaded comments)
  const [replyingTo, setReplyingTo] = useState(null); // { id, username } of comment being replied to
  const [replyText, setReplyText] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [expandedReplies, setExpandedReplies] = useState({}); // Track which comments have expanded replies
  
  // Premium subscription state (for ad-free experience)
  const [hasPremiumSubscription, setHasPremiumSubscription] = useState(false);

  // Auto-fullscreen on landscape rotation state (YouTube-like feature)
  const [autoFullscreenEnabled, setAutoFullscreenEnabled] = useState(true); // User preference
  const [isLandscape, setIsLandscape] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const videoContainerRef = useRef(null); // Container for fullscreen
  
  // Landscape controls visibility (tap to show, auto-hide)
  const [showLandscapeControls, setShowLandscapeControls] = useState(false);
  const landscapeControlsTimeoutRef = useRef(null);

  // Creator subscription state (for videos linked to creators)
  const [creatorProfile, setCreatorProfile] = useState(null);
  const [isSubscribedToCreator, setIsSubscribedToCreator] = useState(false);
  const [subscribingToCreator, setSubscribingToCreator] = useState(false);
  const [showCreatorSubscribeModal, setShowCreatorSubscribeModal] = useState(false);
  const [creatorSubscribeEmail, setCreatorSubscribeEmail] = useState('');

  // Next Up recommendation state (lower third overlay near end of video)
  const [nextVideoRecommendation, setNextVideoRecommendation] = useState(null);
  const [showNextUpOverlay, setShowNextUpOverlay] = useState(false);
  const [nextUpDismissed, setNextUpDismissed] = useState(false);
  const nextUpFetchedRef = useRef(false); // Prevent multiple fetches
  
  // Refs to access latest state values in event handlers (avoids stale closures)
  const videoRef = useRef(null);
  const creatorProfileRef = useRef(null);
  const relatedVideosRef = useRef([]);
  const nextVideoRecommendationRef = useRef(null);
  
  // Keep refs in sync with state
  useEffect(() => { videoRef.current = video; }, [video]);
  useEffect(() => { creatorProfileRef.current = creatorProfile; }, [creatorProfile]);
  useEffect(() => { relatedVideosRef.current = relatedVideos; }, [relatedVideos]);
  useEffect(() => { nextVideoRecommendationRef.current = nextVideoRecommendation; }, [nextVideoRecommendation]);
  
  // Ref to track if overlay is shown (to avoid stale closure in time update handler)
  const showNextUpOverlayRef = useRef(false);
  useEffect(() => { showNextUpOverlayRef.current = showNextUpOverlay; }, [showNextUpOverlay]);
  
  // Ref for nextUpDismissed to avoid stale closure in time update handler
  const nextUpDismissedRef = useRef(false);
  useEffect(() => { nextUpDismissedRef.current = nextUpDismissed; }, [nextUpDismissed]);
  
  // Determine if ads should be shown - NO ADS for premium subscribers or premium tier videos
  const shouldShowAds = video && video.tier !== 'premium' && !hasPremiumSubscription;
  
  // Video Ads Hook - DISABLED since we're using VODPlayer with built-in IMA support
  // Providing default values for all the variables that were returned by the hook
  const inlineAdPlayingFromHook = false;
  const adPaused = false;
  const imaReady = false;
  const adSettings = null;
  const videoCuePoints = null;
  const playInlinePreRoll = () => false;
  const skipCustomAd = () => {};
  const showSkipButton = false;
  const skipCountdown = 0;
  const customAd = null;
  const adType = null;
  const toggleAdPlayPause = () => {};
  const resetInlineAds = () => {};
  const resizeImaAds = () => {};
  const hasCustomAdSettings = false;
  const cuePointsLoaded = true;

  // Debug: Log ad settings when they change
  useEffect(() => {
    if (adSettings) {
      console.log('[VODVideoDetail] Ad settings loaded:', {
        break1: adSettings.ad_break_1_enabled,
        break2: `${adSettings.ad_break_2_enabled} at ${adSettings.ad_break_2_time}s`,
        break3: `${adSettings.ad_break_3_enabled} at ${adSettings.ad_break_3_time}s`,
        rotation: adSettings.rotation_percentage
      });
    }
  }, [adSettings]);

  // Mobile detection - use user agent to detect actual mobile devices
  // This ensures mobile detection doesn't change when rotating to landscape
  useEffect(() => {
    const checkMobile = () => {
      // Check user agent for mobile devices - this is the most reliable method
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      // Also check for touch capability combined with NOT being a desktop OS
      const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isDesktopOS = /Windows NT|Macintosh|Linux x86/i.test(navigator.userAgent) && !/Android/i.test(navigator.userAgent);
      
      // Mobile if: user agent says mobile, OR has touch but not a desktop OS
      const result = isMobileDevice || (hasTouchScreen && !isDesktopOS);
      console.log('[Mobile Detection] isMobileDevice:', isMobileDevice, 'hasTouchScreen:', hasTouchScreen, 'isDesktopOS:', isDesktopOS, 'Result:', result);
      setIsMobile(result);
    };
    checkMobile();
    // Only check on initial load, not on resize (orientation change shouldn't affect this)
  }, []);

  // Auto-fullscreen on landscape rotation (YouTube-like feature)
  // Only works on mobile devices when video is playing
  // Uses CSS-based fullscreen to allow ads to display properly
  useEffect(() => {
    if (!isMobile) return; // Only on mobile devices
    
    // Handle orientation change - CSS-based fullscreen + native Fullscreen API
    const handleOrientationChange = () => {
      const isNowLandscape = window.matchMedia('(orientation: landscape)').matches;
      setIsLandscape(isNowLandscape);
      
      // Only auto-fullscreen if enabled and video is playing
      if (!autoFullscreenEnabled) return;
      
      const videoElement = videoPlayerRef.current;
      const isVideoPlaying = playingInline && videoElement;
      
      if (isNowLandscape && isVideoPlaying) {
        // Entering landscape while video is playing - go CSS fullscreen
        setIsFullscreen(true);
        
        // Also request native fullscreen API to hide browser chrome
        const container = videoContainerRef.current?.parentElement;
        if (container && !document.fullscreenElement && !document.webkitFullscreenElement) {
          try {
            if (container.requestFullscreen) {
              container.requestFullscreen().catch(() => {});
            } else if (container.webkitRequestFullscreen) {
              container.webkitRequestFullscreen();
            }
          } catch {}
        }
        
        // Resize IMA ads after container has resized
        setTimeout(() => {
          if (resizeImaAds) resizeImaAds();
        }, 200);
      } else if (!isNowLandscape && isFullscreen) {
        // Returning to portrait - exit CSS fullscreen
        setIsFullscreen(false);
        
        // Exit native fullscreen if active
        if (document.fullscreenElement || document.webkitFullscreenElement) {
          try {
            if (document.exitFullscreen) {
              document.exitFullscreen().catch(() => {});
            } else if (document.webkitExitFullscreen) {
              document.webkitExitFullscreen();
            }
          } catch {}
        }
        
        // Resize IMA ads for portrait mode
        setTimeout(() => {
          if (resizeImaAds) resizeImaAds();
        }, 200);
      }
    };
    
    // Listen for orientation changes
    const mediaQuery = window.matchMedia('(orientation: landscape)');
    
    // Initial check
    setIsLandscape(mediaQuery.matches);
    
    // Modern API
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleOrientationChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleOrientationChange);
    }
    
    // Also listen for resize (backup for orientation)
    window.addEventListener('resize', handleOrientationChange);
    
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleOrientationChange);
      } else {
        mediaQuery.removeListener(handleOrientationChange);
      }
      window.removeEventListener('resize', handleOrientationChange);
    };
  }, [isMobile, autoFullscreenEnabled, playingInline, isFullscreen, resizeImaAds]);

  // Orientation change state preservation for video player
  const playbackStateRef = useRef({
    wasPlaying: false,
    currentTime: 0,
    isMuted: false,
    volume: 1,
    isFullscreen: false
  });
  
  // Track if we're in the middle of an orientation change
  const isRotatingRef = useRef(false);
  
  // Handle orientation change - preserve playback state seamlessly
  useEffect(() => {
    const savePlaybackState = () => {
      const activePlayer = videoPlayerRef.current || trailerRef.current;
      if (activePlayer && !activePlayer.paused) {
        playbackStateRef.current = {
          wasPlaying: !activePlayer.paused,
          currentTime: activePlayer.currentTime,
          isMuted: activePlayer.muted,
          volume: activePlayer.volume,
          isFullscreen: !!document.fullscreenElement
        };
        isRotatingRef.current = true;
        console.log('[VODVideoDetail] Orientation changing, saving state:', playbackStateRef.current);
      }
    };
    
    const restorePlaybackState = () => {
      if (!isRotatingRef.current) return;
      
      const activePlayer = videoPlayerRef.current || trailerRef.current;
      if (activePlayer && playbackStateRef.current.wasPlaying) {
        // Immediately try to continue playback without seeking if time difference is small
        const timeDiff = Math.abs(activePlayer.currentTime - playbackStateRef.current.currentTime);
        
        // Only seek if significant time difference (>1 second)
        if (timeDiff > 1) {
          activePlayer.currentTime = playbackStateRef.current.currentTime;
        }
        
        activePlayer.muted = playbackStateRef.current.isMuted;
        activePlayer.volume = playbackStateRef.current.volume;
        
        // Resume playback immediately
        const playPromise = activePlayer.play();
        if (playPromise !== undefined) {
          playPromise.catch(err => {
            console.log('[VODVideoDetail] Resume after rotation failed, trying muted:', err);
            // If autoplay fails, try muted
            activePlayer.muted = true;
            activePlayer.play().catch(e => console.log('[VODVideoDetail] Muted play also failed:', e));
          });
        }
        
        console.log('[VODVideoDetail] Playback restored after orientation change');
      }
      
      isRotatingRef.current = false;
    };
    
    // Handle orientation change start
    const handleOrientationChangeStart = () => {
      savePlaybackState();
    };
    
    // Handle orientation change complete with minimal delay
    const handleOrientationChangeEnd = () => {
      // Use requestAnimationFrame for smoother restoration
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          restorePlaybackState();
        });
      });
    };
    
    // Listen for orientation change
    window.addEventListener('orientationchange', handleOrientationChangeStart);
    
    // Also handle resize for orientation changes
    let resizeTimeout;
    let lastWidth = window.innerWidth;
    
    const handleResize = () => {
      const currentWidth = window.innerWidth;
      // Only trigger on significant width changes (orientation change)
      if (Math.abs(currentWidth - lastWidth) > 100) {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(handleOrientationChangeEnd, 50);
        lastWidth = currentWidth;
      }
    };
    window.addEventListener('resize', handleResize);
    
    // Screen orientation API for modern browsers
    if (screen.orientation) {
      screen.orientation.addEventListener('change', handleOrientationChangeStart);
    }
    
    // Handle visibility change (sometimes triggered during rotation)
    const handleVisibilityChange = () => {
      if (!document.hidden && isRotatingRef.current) {
        restorePlaybackState();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('orientationchange', handleOrientationChangeStart);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (screen.orientation) {
        screen.orientation.removeEventListener('change', handleOrientationChangeStart);
      }
      clearTimeout(resizeTimeout);
    };
  }, []);

  // Listen for fullscreen change events (for ESC key exit, etc.)
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isNowFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement);
      console.log('[Fullscreen] Change detected, isFullscreen:', isNowFullscreen);
      setIsFullscreen(isNowFullscreen);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);


  useEffect(() => {
    const token = localStorage.getItem('livestreamToken');
    setIsLoggedIn(!!token);
    if (token) {
      // Load from localStorage first for immediate display
      const customerData = localStorage.getItem('livestreamCustomer');
      if (customerData) {
        setCustomer(JSON.parse(customerData));
      }
      
      // Then fetch fresh profile from server to get latest username
      fetchCustomerProfile(token);
      
      // Fetch watch progress for resume feature (only for logged-in users)
      if (videoId || slug) {
        fetchWatchProgress(slug || videoId);
      } else {
        setWatchProgressLoaded(true);
      }
      
      // Auto-purchase after Google OAuth redirect
      const params = new URLSearchParams(window.location.search);
      if (params.get('auto_purchase') === 'true') {
        // Clean the URL
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, '', cleanUrl);
        // Trigger purchase after video loads
        setPendingPurchaseAfterAuth(true);
      }
      
      // Check for pending promo code from pre-login redirect
      const pendingPromo = sessionStorage.getItem('pendingPromoCode');
      if (pendingPromo) {
        (async () => { try {
          const promoData = JSON.parse(pendingPromo);
          sessionStorage.removeItem('pendingPromoCode');
          // Auto-apply and redeem the promo code
          setPromoCode(promoData.code);
          // Validate first, then redeem
          const valRes = await axios.post(`${API}/api/promo-codes/validate`, {
            code: promoData.code,
            video_id: promoData.video_id
          });
          if (valRes.data.valid && valRes.data.is_free) {
            const redeemRes = await axios.post(`${API}/api/promo-codes/redeem`, {
              code: promoData.code,
              video_id: promoData.video_id
            }, { headers: { Authorization: `Bearer ${token}` } });
            if (redeemRes.data.access_granted) {
              setShowPaywall(false);
            }
          }
        } catch (promoErr) {
          console.warn('Failed to auto-redeem pending promo:', promoErr);
        } })();
      }
    } else {
      setWatchProgressLoaded(true);
    }
    // Check if signup prompt was already shown this session
    const promptShown = sessionStorage.getItem('vodSignupPromptShown');
    if (promptShown) {
      setSignupPromptShown(true);
    }
    // Fetch popup settings
    fetchPopupSettings();
    fetchVideo();
  }, [videoId, slug]);
  
  // Fetch popup settings from backend
  const fetchPopupSettings = async () => {
    try {
      const res = await axios.get(`${API}/api/vod/popup/settings`);
      if (res.data) {
        setPopupSettings(res.data);
      }
    } catch (err) {
      console.error('Error fetching popup settings:', err);
      // Keep popup disabled if fetch fails (safe default)
    } finally {
      setPopupSettingsLoaded(true);
    }
  };
  
  // Fetch watch progress for logged-in users (to show resume option)
  const fetchWatchProgress = async (videoIdOrSlug) => {
    const token = localStorage.getItem('livestreamToken');
    if (!token) {
      setWatchProgressLoaded(true);
      return;
    }
    
    try {
      const res = await axios.get(`${API}/api/vod/watch-progress/${videoIdOrSlug}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Only show resume prompt if user has significant progress (> 30 seconds and < 95%)
      if (res.data && res.data.progress > 30 && res.data.completion_rate < 95) {
        setSavedProgress(res.data);
        // Always show the resume prompt — even on autoplay returns
        setShowResumePrompt(true);
      }
    } catch (err) {
      // No watch history found - that's fine
      console.log('No watch progress found');
    } finally {
      setWatchProgressLoaded(true);
    }
  };
  
  // Handle resume choice
  const handleResumeChoice = (resume) => {
    setShowResumePrompt(false);
    
    // Skip pre-roll ad when resuming — user already saw it on first visit
    if (resume) {
      setSkipPreRollOnResume(true);
    }
    
    // Paywall check — paid videos require purchase before playback
    const videoPrice = video?.price || 0;
    const isFreeVideo = videoPrice <= 0;
    const hasPurchased = video?.has_purchased || video?.access_type === 'purchase';
    if (!isFreeVideo && !hasPurchased && !isSharedLink && !video?.free_preview) {
      setShowPaywall(true);
      return;
    }
    
    setPlayingInline(true);
    
    // Wait for VODPlayer to mount and be ready, then seek
    const seekToPosition = () => {
      if (videoPlayerRef.current) {
        if (resume && savedProgress) {
          videoPlayerRef.current.seek(savedProgress.progress);
        } else {
          videoPlayerRef.current.seek(0);
        }
        videoPlayerRef.current.play().catch(() => {});
        setVideoPlaying(true);
      }
    };
    
    // Try multiple times to ensure the player is mounted
    setTimeout(() => {
      seekToPosition();
      // Retry after player's autoplay delay (in case first seek gets overridden)
      setTimeout(seekToPosition, 300);
    }, 600);
  };
  
  // Auto-play video when loaded with autoplay parameter (handles return from signup)
  useEffect(() => {
    if (video && shouldAutoPlay && !loading && watchProgressLoaded) {
      // If resume prompt is being shown, don't autoplay — let user choose
      if (showResumePrompt && savedProgress) {
        return;
      }
      
      // Paywall check — paid videos require purchase before autoplay
      const videoPrice = video?.price || 0;
      const isFreeVideo = videoPrice <= 0;
      const hasPurchased = video?.has_purchased || video?.access_type === 'purchase';
      if (!isFreeVideo && !hasPurchased && !video?.free_preview) {
        setShowPaywall(true);
        return;
      }
      
      // Small delay to ensure video data is ready
      const timer = setTimeout(() => {
        // Start inline playback
        setPlayingInline(true);
        
        // If there's a resume timestamp from URL, seek to it after video starts
        if (resumeTimestamp && resumeTimestamp > 0) {
          setTimeout(() => {
            if (videoPlayerRef.current) {
              videoPlayerRef.current.seek(resumeTimestamp);
              videoPlayerRef.current.play().catch(() => {});
              setVideoPlaying(true);
            }
          }, 1000);
        } else {
          // Check sessionStorage for saved resume position
          const savedResume = sessionStorage.getItem('vodResumeVideo');
          if (savedResume) {
            try {
              const resumeData = JSON.parse(savedResume);
              if (resumeData.shouldAutoPlay && resumeData.timestamp > 0) {
                setTimeout(() => {
                  if (videoPlayerRef.current) {
                    videoPlayerRef.current.seek(resumeData.timestamp);
                    videoPlayerRef.current.play().catch(() => {});
                    setVideoPlaying(true);
                  }
                }, 1000);
              }
              // Clear the saved resume data
              sessionStorage.removeItem('vodResumeVideo');
            } catch (e) {
              console.error('Error parsing resume data:', e);
            }
          }
        }
        
        // Clean URL parameters after using them
        if (resumeTimestamp || shouldAutoPlay) {
          const cleanUrl = window.location.pathname;
          window.history.replaceState({}, '', cleanUrl);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [video, shouldAutoPlay, loading, resumeTimestamp, showResumePrompt, savedProgress, watchProgressLoaded]);
  
  // Fetch latest customer profile from server (includes username)
  const fetchCustomerProfile = async (token) => {
    try {
      const res = await axios.get(`${API}/api/customer/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data) {
        // Update customer state with fresh data from server
        const updatedCustomer = {
          ...JSON.parse(localStorage.getItem('livestreamCustomer') || '{}'),
          ...res.data,
          username: res.data.username // Ensure username is included
        };
        setCustomer(updatedCustomer);
        localStorage.setItem('livestreamCustomer', JSON.stringify(updatedCustomer));
      }
    } catch (err) {
      console.error('Error fetching customer profile:', err);
    }
  };
  
  // Fetch subscription status to determine if user has premium (no ads)
  const fetchSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('livestreamToken');
      if (!token) {
        setHasPremiumSubscription(false);
        return;
      }
      
      const res = await axios.get(`${API}/api/vod/membership/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Check if user has active premium subscription
      if (res.data.subscription && res.data.subscription.tier === 'premium' && res.data.subscription.status === 'active') {
        setHasPremiumSubscription(true);
        console.log('[VODVideoDetail] User has premium subscription - ads disabled');
      } else {
        setHasPremiumSubscription(false);
      }
    } catch (err) {
      console.error('Error fetching subscription status:', err);
      setHasPremiumSubscription(false);
    }
  };

  // Fetch reviews when video is loaded
  useEffect(() => {
    if (video?.id) {
      fetchReviews();
      fetchReactions();
    }
  }, [video?.id]);

  // Fetch creator profile when video has creator_id
  useEffect(() => {
    if (video?.creator_id) {
      fetchCreatorProfile(video.creator_id);
    } else {
      setCreatorProfile(null);
    }
  }, [video?.creator_id]);

  // Check watchlist status when customer is loaded
  useEffect(() => {
    if (customer && video?.id) {
      checkWatchlistStatus();
    }
  }, [customer, video?.id]);
  
  // Fetch subscription status when customer is loaded (to determine ad-free status)
  useEffect(() => {
    if (customer) {
      fetchSubscriptionStatus();
    }
  }, [customer]);
  
  // Track watch time when component unmounts or page is closed
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Track ACTUAL watch time (not position) before page closes
      if (playingInline && watchTrackingRef.current.totalWatched > 5) {
        const totalDuration = videoPlayerRef.current?.duration || video?.duration || 0;
        const token = localStorage.getItem('livestreamToken');
        const trackData = {
          video_id: video?.id,
          watch_duration: Math.floor(watchTrackingRef.current.totalWatched),
          total_duration: Math.floor(totalDuration),
          current_position: Math.floor(videoPlayerRef.current?.currentTime || 0),
          referrer: sessionStorage.getItem('initial_referrer') || 'Direct',
          device_type: /Mobile|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
          browser: navigator.userAgent.includes('Chrome') ? 'Chrome' : 'Other'
        };
        const blob = new Blob([JSON.stringify(trackData)], { type: 'application/json' });
        const url = token 
          ? `${API}/api/vod/track-watch?token=${encodeURIComponent(token)}`
          : `${API}/api/vod/track-watch`;
        navigator.sendBeacon(url, blob);
      }
    };
    
    // Also save progress when user switches tabs/apps (critical for mobile)
    const handleVisibilityChange = () => {
      if (document.hidden && playingInline && watchTrackingRef.current.totalWatched > 5) {
        const totalDuration = videoPlayerRef.current?.duration || video?.duration || 0;
        trackWatchEvent(watchTrackingRef.current.totalWatched, totalDuration);
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      // Also track when component unmounts (navigation)
      if (playingInline && watchTrackingRef.current.totalWatched > 5) {
        const totalDuration = videoPlayerRef.current?.duration || video?.duration || 0;
        trackWatchEvent(watchTrackingRef.current.totalWatched, totalDuration);
      }
    };
  }, [playingInline, video?.id, video?.duration]);

  // Periodic progress saving every 15 seconds — saves resume position only, NOT analytics.
  // Analytics watch time is tracked separately via watchTrackingRef.totalWatched in onTimeUpdate.
  // Sending currentTime (video position) as watch_duration was inflating analytics because
  // position values (e.g. 7200 at 2h mark) mixed with totalWatched values (e.g. 30s actual)
  // caused the backend session calculator to compute massive false deltas.
  useEffect(() => {
    if (!playingInline || !video?.id) return;
    
    const saveProgressInterval = setInterval(() => {
      const ct = videoPlayerRef.current?.currentTime || 0;
      const dur = videoPlayerRef.current?.duration || video?.duration || 0;
      if (ct > 10 && dur > 0) {
        // Save progress position via the progress API (not the watch analytics API)
        const token = localStorage.getItem('livestreamToken');
        axios.post(
          `${API}/api/vod/videos/${video.id}/progress`,
          { position: Math.floor(ct), duration: Math.floor(dur) },
          token ? { headers: { Authorization: `Bearer ${token}` } } : {}
        ).catch(() => {});
      }
    }, 15000);
    
    return () => clearInterval(saveProgressInterval);
  }, [playingInline, video?.id, video?.duration]);

  const fetchVideo = async () => {
    setLoading(true);
    // Reset Next Up state when loading a new video
    setNextVideoRecommendation(null);
    setShowNextUpOverlay(false);
    setNextUpDismissed(false);
    nextUpFetchedRef.current = false;
    
    // CRITICAL: Reset ad state when loading a new video
    preRollAttemptedRef.current = false;
    preRollCompletedRef.current = false;
    isRestoringContentRef.current = false;
    
    try {
      const token = localStorage.getItem('livestreamToken') || localStorage.getItem('adminToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Determine if we have a slug or UUID
      // UUIDs are 36 characters with format xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
      // Slugs contain dashes but aren't UUIDs
      const identifier = slug || videoId;
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(identifier);
      
      let endpoint;
      if (slug) {
        // Explicitly provided slug parameter from /vod/v/:slug route
        endpoint = `${API}/api/vod/video/by-slug/${slug}`;
      } else if (videoId && isUUID) {
        // UUID-style videoId - use direct video endpoint
        endpoint = `${API}/api/vod/videos/${videoId}`;
      } else if (videoId) {
        // videoId contains a slug (from /vod/video/:videoId route with slug value)
        endpoint = `${API}/api/vod/video/by-slug/${videoId}`;
      } else {
        throw new Error('No video identifier provided');
      }
      
      const res = await axios.get(endpoint, { headers });
      setVideo(res.data);
      
      // Fetch related videos from same category
      if (res.data.category_id) {
        const relatedRes = await axios.get(
          `${API}/api/vod/videos?category=${res.data.category_id}&limit=6`,
          { headers }
        );
        setRelatedVideos(relatedRes.data.videos?.filter(v => v.id !== res.data.id) || []);
      }
    } catch (err) {
      console.error('Error fetching video:', err);
      if (err.response?.status === 404) {
        navigate('/vod');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    if (!video?.id) return;
    try {
      const res = await axios.get(`${API}/api/vod/videos/${video.id}/reviews`);
      setReviews(res.data.reviews || []);
      setReviewStats({
        average_rating: res.data.average_rating || 0,
        review_count: res.data.review_count || 0
      });
      
      // Check if user has already reviewed
      const token = localStorage.getItem('livestreamToken');
      if (token) {
        const customerData = localStorage.getItem('livestreamCustomer');
        if (customerData) {
          const customerId = JSON.parse(customerData).id;
          const userReview = res.data.reviews.find(r => r.customer_id === customerId);
          if (userReview) {
            setUserHasReviewed(true);
            setUserRating(userReview.rating);
            setUserReviewText(userReview.review_text || '');
          }
        }
      }
    } catch (err) {
      console.error('Error fetching reviews:', err);
    }
  };

  // Fetch like/dislike reactions
  const fetchReactions = async () => {
    if (!video?.id) return;
    try {
      const token = localStorage.getItem('livestreamToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const res = await axios.get(`${API}/api/vod/videos/${video.id}/reactions`, { headers });
      setLikeCount(res.data.likes || 0);
      setDislikeCount(res.data.dislikes || 0);
      setUserReaction(res.data.user_reaction || null);
    } catch (err) {
      console.error('Error fetching reactions:', err);
    }
  };

  // Fetch creator profile if video is linked to a creator
  const fetchCreatorProfile = async (creatorId) => {
    if (!creatorId) {
      setCreatorProfile(null);
      return;
    }
    
    try {
      console.log('Fetching creator profile for:', creatorId);
      const res = await axios.get(`${API}/api/creators/profiles/${creatorId}`);
      console.log('Creator profile response:', res.data);
      setCreatorProfile(res.data.profile);
      
      // Check subscription status
      const storedEmail = localStorage.getItem('user_email');
      const customerData = localStorage.getItem('livestreamCustomer');
      const email = storedEmail || (customerData ? JSON.parse(customerData).email : null);
      
      if (email) {
        setCreatorSubscribeEmail(email);
        try {
          const subRes = await axios.get(`${API}/api/creators/subscription-status`, {
            params: { email, creator_id: creatorId }
          });
          setIsSubscribedToCreator(subRes.data.is_subscribed);
        } catch (err) {
          console.error('Error checking subscription:', err);
        }
      }
    } catch (err) {
      console.error('Error fetching creator profile:', err);
    }
  };

  // Subscribe to creator
  const handleSubscribeToCreator = async () => {
    if (!creatorSubscribeEmail) {
      setShowCreatorSubscribeModal(true);
      return;
    }
    
    setSubscribingToCreator(true);
    try {
      await axios.post(`${API}/api/creators/subscribe`, {
        email: creatorSubscribeEmail,
        creator_id: video.creator_id,
        notify_new_videos: true,
        notify_premieres: true,
        notify_posts: true
      });
      
      setIsSubscribedToCreator(true);
      setShowCreatorSubscribeModal(false);
      localStorage.setItem('user_email', creatorSubscribeEmail);
      
      // Update creator profile to reflect new subscriber count
      setCreatorProfile(prev => prev ? { ...prev, subscriber_count: (prev.subscriber_count || 0) + 1 } : null);
    } catch (err) {
      console.error('Error subscribing to creator:', err);
    } finally {
      setSubscribingToCreator(false);
    }
  };

  // Unsubscribe from creator
  const handleUnsubscribeFromCreator = async () => {
    const email = creatorSubscribeEmail || localStorage.getItem('user_email');
    if (!email) return;
    
    try {
      await axios.post(`${API}/api/creators/unsubscribe`, null, {
        params: { email, creator_id: video.creator_id }
      });
      setIsSubscribedToCreator(false);
      setCreatorProfile(prev => prev ? { ...prev, subscriber_count: Math.max(0, (prev.subscriber_count || 1) - 1) } : null);
    } catch (err) {
      console.error('Error unsubscribing:', err);
    }
  };

  // Fetch next video recommendation for "Next Up" overlay
  const fetchNextVideoRecommendation = async (currentVideo, currentCreatorProfile, currentRelatedVideos) => {
    if (nextUpFetchedRef.current || !currentVideo) return;
    nextUpFetchedRef.current = true;
    
    console.log('[NextUp] Fetching recommendation for video:', currentVideo.title);
    console.log('[NextUp] Creator ID:', currentVideo.creator_id, 'Creator Profile:', currentCreatorProfile?.name);
    
    try {
      // Priority 1: If video has a creator, try to get another video from the same creator
      if (currentVideo.creator_id) {
        // First try with creatorProfile if available, otherwise fetch directly
        let creatorVideos = [];
        
        if (currentCreatorProfile?.slug) {
          const creatorVideosRes = await axios.get(`${API}/api/creators/profiles/slug/${currentCreatorProfile.slug}`);
          creatorVideos = creatorVideosRes.data.videos || [];
        } else {
          // Try to fetch creator's videos by creator_id
          try {
            const creatorRes = await axios.get(`${API}/api/creators/profiles/${currentVideo.creator_id}`);
            const creatorData = creatorRes.data?.profile || creatorRes.data;
            if (creatorData?.slug) {
              const creatorVideosRes = await axios.get(`${API}/api/creators/profiles/slug/${creatorData.slug}`);
              creatorVideos = creatorVideosRes.data.videos || [];
            }
          } catch (e) {
            console.log('[NextUp] Could not fetch creator details:', e.message);
          }
        }
        
        // Filter out the current video and get a random one
        const otherVideos = creatorVideos.filter(v => v.id !== currentVideo.id && v.slug !== currentVideo.slug);
        
        if (otherVideos.length > 0) {
          // Pick a random video from the creator's other videos
          const nextVideo = otherVideos[Math.floor(Math.random() * otherVideos.length)];
          setNextVideoRecommendation({
            ...nextVideo,
            recommendation_source: 'creator',
            creator_name: currentCreatorProfile?.name || 'Creator'
          });
          console.log('[NextUp] Recommending video from same creator:', nextVideo.title);
          return;
        }
      }
      
      // Priority 2: Fallback to related videos (from same category)
      if (currentRelatedVideos && currentRelatedVideos.length > 0) {
        const filtered = currentRelatedVideos.filter(v => v.id !== currentVideo.id && v.slug !== currentVideo.slug);
        if (filtered.length > 0) {
          const nextVideo = filtered[Math.floor(Math.random() * filtered.length)];
          setNextVideoRecommendation({
            ...nextVideo,
            recommendation_source: 'related'
          });
          console.log('[NextUp] Recommending related video:', nextVideo.title);
          return;
        }
      }
      
      // Priority 3: Fetch random videos from library
      const randomRes = await axios.get(`${API}/api/vod/videos`, { params: { limit: 10 } });
      const randomVideos = (randomRes.data.videos || []).filter(v => v.id !== currentVideo.id && v.slug !== currentVideo.slug);
      
      if (randomVideos.length > 0) {
        const nextVideo = randomVideos[Math.floor(Math.random() * randomVideos.length)];
        setNextVideoRecommendation({
          ...nextVideo,
          recommendation_source: 'library'
        });
        console.log('[NextUp] Recommending random video from library:', nextVideo.title);
      } else {
        console.log('[NextUp] No videos available for recommendation');
      }
    } catch (err) {
      console.error('[NextUp] Error fetching recommendation:', err);
    }
  };

  // Handle like/dislike reaction
  const handleReaction = async (reactionType) => {
    const token = localStorage.getItem('livestreamToken');
    if (!token) {
      setShowSignInModal(true);
      return;
    }
    
    setReactionLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/vod/videos/${video.id}/react`,
        { reaction_type: reactionType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update counts based on response
      setLikeCount(res.data.likes || 0);
      setDislikeCount(res.data.dislikes || 0);
      setUserReaction(res.data.user_reaction);
    } catch (err) {
      console.error('Error reacting to video:', err);
    } finally {
      setReactionLoading(false);
    }
  };

  const checkWatchlistStatus = async () => {
    try {
      const token = localStorage.getItem('livestreamToken');
      if (!token) return;
      
      // Use video.id (actual ID) for watchlist API
      const vidId = video?.id || videoId;
      const res = await axios.get(
        `${API}/api/vod/watchlist/check/${vidId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInWatchlist(res.data.in_watchlist);
    } catch (err) {
      console.error('Error checking watchlist:', err);
    }
  };

  const toggleWatchlist = async () => {
    if (!customer) {
      navigate(getLoginUrl(`/vod/video/${video?.slug || video?.id || videoId}`));
      return;
    }
    
    setWatchlistLoading(true);
    try {
      const token = localStorage.getItem('livestreamToken');
      // Use video.id (actual ID) for watchlist API
      const vidId = video?.id || videoId;
      
      if (inWatchlist) {
        await axios.delete(`${API}/api/vod/watchlist/${vidId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setInWatchlist(false);
      } else {
        await axios.post(`${API}/api/vod/watchlist/${vidId}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setInWatchlist(true);
      }
    } catch (err) {
      console.error('Error toggling watchlist:', err);
    } finally {
      setWatchlistLoading(false);
    }
  };
  
  // Premiere reminder functions
  const fetchReminderStatus = async () => {
    if (!video?.id || !video?.is_premiering_soon) return;
    
    try {
      const token = localStorage.getItem('livestreamToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const res = await axios.get(`${API}/api/vod/videos/${video.id}/remind`, { headers });
      setHasReminder(res.data.has_reminder || false);
      setReminderCount(res.data.reminder_count || 0);
    } catch (err) {
      console.error('Error fetching reminder status:', err);
    }
  };
  
  const toggleReminder = async () => {
    const token = localStorage.getItem('livestreamToken');
    if (!token) {
      setShowSignInModal(true);
      return;
    }
    
    setReminderLoading(true);
    try {
      if (hasReminder) {
        await axios.delete(`${API}/api/vod/videos/${video.id}/remind`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setHasReminder(false);
        setReminderCount(prev => Math.max(0, prev - 1));
      } else {
        await axios.post(`${API}/api/vod/videos/${video.id}/remind`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setHasReminder(true);
        setReminderCount(prev => prev + 1);
      }
    } catch (err) {
      console.error('Error toggling reminder:', err);
    } finally {
      setReminderLoading(false);
    }
  };
  
  // Premiere countdown timer
  useEffect(() => {
    // Start countdown if video is a premiere with a future date
    // Use helper function to check premiere status robustly
    if (!isVideoPremiere(video)) return;
    
    // Use premiere_countdown from API, or calculate from premiere_date
    let initialCountdown = video.premiere_countdown || getPremiereCountdownSeconds(video);
    
    if (!initialCountdown || initialCountdown <= 0) return;
    
    setPremiereCountdown(initialCountdown);
    
    const interval = setInterval(() => {
      setPremiereCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          // Refresh video data when countdown ends
          window.location.reload();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, [video?.is_premiering_soon, video?.premiere_countdown, video?.premiere_date, video?.visibility_status]);
  
  // Fetch reminder status when video loads
  useEffect(() => {
    if (isVideoPremiere(video)) {
      fetchReminderStatus();
    }
  }, [video?.id, video?.is_premiering_soon, video?.visibility_status]);

  // Save username from modal
  const saveUsername = async () => {
    if (!newUsername.trim()) {
      setUsernameError('Please enter a username');
      return;
    }

    // Validate username (alphanumeric, 3-20 chars)
    if (!newUsername.match(/^[a-zA-Z0-9]{3,20}$/)) {
      setUsernameError('Username must be 3-20 alphanumeric characters');
      return;
    }

    setSavingUsername(true);
    setUsernameError('');

    try {
      const token = localStorage.getItem('livestreamToken');
      await axios.put(`${API}/api/customer/profile`, 
        { username: newUsername.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update local state and storage
      const updatedCustomer = { ...customer, username: newUsername.trim() };
      setCustomer(updatedCustomer);
      localStorage.setItem('livestreamCustomer', JSON.stringify(updatedCustomer));
      
      // Close modal and reset
      setShowUsernameModal(false);
      setNewUsername('');
    } catch (err) {
      setUsernameError(err.response?.data?.detail || 'Failed to save username');
    } finally {
      setSavingUsername(false);
    }
  };

  const submitReview = async () => {
    if (!customer) {
      navigate(getLoginUrl(`/vod/video/${video?.slug || video?.id || videoId}`));
      return;
    }
    
    // Check if user has a username set
    if (!customer.username) {
      // Refresh customer data from server to check for username
      try {
        const token = localStorage.getItem('livestreamToken');
        const res = await axios.get(`${API}/api/customer/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.username) {
          // Update local customer data
          const updatedCustomer = { ...customer, username: res.data.username };
          setCustomer(updatedCustomer);
          localStorage.setItem('livestreamCustomer', JSON.stringify(updatedCustomer));
        } else {
          // Still no username, show modal
          setShowUsernameModal(true);
          return;
        }
      } catch (err) {
        // If profile fetch fails, show username modal anyway
        setShowUsernameModal(true);
        return;
      }
    }
    
    if (!userReviewText.trim()) {
      return; // Don't submit empty comments
    }
    
    setSubmittingReview(true);
    setShowEmojiPicker(false);
    try {
      const token = localStorage.getItem('livestreamToken');
      // Capture timestamp if video is currently playing inline
      const currentTimestamp = playingInline ? (videoPlayerRef.current?.currentTime || videoCurrentTime || null) : null;
      
      // Use video.id (actual ID) instead of videoId (URL param which could be undefined for slug URLs)
      await axios.post(
        `${API}/api/vod/videos/${video.id}/reviews`,
        { 
          rating: 5, 
          review_text: userReviewText,
          video_timestamp: currentTimestamp // Include timestamp if watching video
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setUserHasReviewed(true);
      setUserReviewText(''); // Clear the input box after successful post
      
      // Fetch reviews but don't restore user's text to input (we want it empty)
      if (!video?.id) return;
      try {
        const res = await axios.get(`${API}/api/vod/videos/${video.id}/reviews`);
        setReviews(res.data.reviews || []);
        setReviewStats({
          average_rating: res.data.average_rating || 0,
          review_count: res.data.review_count || 0
        });
        // Note: Intentionally NOT setting userReviewText here to keep it empty
      } catch (e) {
        console.error('Error fetching reviews:', e);
      }
    } catch (err) {
      console.error('Error submitting comment:', err);
      alert('Failed to submit comment');
    } finally {
      setSubmittingReview(false);
    }
  };

  const deleteMyReview = async () => {
    if (!confirm('Are you sure you want to delete your comment?')) return;
    
    try {
      const token = localStorage.getItem('livestreamToken');
      // Use video.id (actual ID) instead of videoId (URL param)
      await axios.delete(`${API}/api/vod/videos/${video.id}/reviews`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setUserHasReviewed(false);
      setUserRating(0);
      setUserReviewText('');
      fetchReviews();
    } catch (err) {
      console.error('Error deleting review:', err);
    }
  };

  // Quick comment from video player popup
  const submitQuickComment = async () => {
    if (!customer) {
      setShowQuickComment(false);
      setShowMobileCommentModal(false);
      navigate(getLoginUrl(`/vod/video/${video?.slug || video?.id || videoId}`));
      return;
    }
    
    // Check if user has a username set
    if (!customer.username) {
      setShowQuickComment(false);
      setShowMobileCommentModal(false);
      setShowUsernameModal(true);
      return;
    }
    
    if (!quickCommentText.trim()) return;
    
    setQuickCommentSubmitting(true);
    try {
      const token = localStorage.getItem('livestreamToken');
      // Use the captured timestamp: mobile timestamp, inline timestamp, or current time
      const currentTimestamp = isMobile 
        ? mobileCommentTimestamp 
        : (inlineCommentTimestamp || videoPlayerRef.current?.currentTime || videoCurrentTime || 0);
      
      console.log('[VODVideoDetail] Submitting comment with timestamp:', currentTimestamp);
      
      // Use video.id (actual ID) instead of videoId (URL param)
      await axios.post(
        `${API}/api/vod/videos/${video.id}/reviews`,
        { 
          rating: 5, 
          review_text: quickCommentText,
          video_timestamp: Math.floor(currentTimestamp) // Ensure it's an integer
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Show success state
      setQuickCommentSuccess(true);
      setUserHasReviewed(true);
      setUserReviewText(quickCommentText);
      fetchReviews();
      
      // Auto-close after 2 seconds
      setTimeout(() => {
        setShowQuickComment(false);
        setShowMobileCommentModal(false);
        setQuickCommentSuccess(false);
        setQuickCommentText('');
        // Resume video on mobile if it was playing
        if (isMobile && videoWasPlaying && videoPlayerRef.current) {
          videoPlayerRef.current.play();
          setVideoPlaying(true);
        }
      }, 2000);
    } catch (err) {
      console.error('Error submitting quick comment:', err);
      alert('Failed to submit comment');
    } finally {
      setQuickCommentSubmitting(false);
    }
  };

  // Open mobile comment modal - DON'T pause video, let it continue playing
  const openMobileCommentModal = () => {
    if (!customer) {
      navigate(getLoginUrl(`/vod/video/${video?.slug || video?.id || videoId}`));
      return;
    }
    
    // Capture current timestamp for the comment
    const currentTime = videoPlayerRef.current?.currentTime || videoCurrentTime || 0;
    
    // Don't pause video - let it continue playing
    setMobileCommentTimestamp(currentTime);
    setVideoWasPlaying(videoPlaying); // Track if video was playing (for reference only)
    setShowMobileCommentModal(true);
  };

  // Close mobile comment modal - video continues playing (no resume needed)
  const closeMobileCommentModal = (resumeVideo = true) => {
    setShowMobileCommentModal(false);
    setQuickCommentText('');
    setShowMobileEmojiPicker(false);
    // Video is already playing, no need to resume
  };

  // Submit a reply to a comment
  const submitReply = async (parentId) => {
    if (!replyText.trim()) return;
    if (!customer) {
      navigate(getLoginUrl(`/vod/video/${video?.slug || video?.id || videoId}`));
      return;
    }
    
    setSubmittingReply(true);
    try {
      const token = localStorage.getItem('livestreamToken');
      const response = await axios.post(
        `${API}/api/vod/videos/${video.id}/reviews`,
        {
          rating: 5, // Default rating for replies
          review_text: replyText,
          video_timestamp: null, // Replies don't have timestamps
          parent_id: parentId
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state - add the reply to the parent comment
      setReviews(prevReviews => {
        return prevReviews.map(review => {
          if (review.id === parentId) {
            const newReply = response.data.review || {
              id: response.data.review_id,
              customer_username: customer.username,
              customer_name: customer.username || customer.full_name,
              review_text: replyText,
              created_at: new Date().toISOString(),
              parent_id: parentId
            };
            return {
              ...review,
              replies: [...(review.replies || []), newReply],
              reply_count: (review.reply_count || 0) + 1
            };
          }
          return review;
        });
      });
      
      // Clear reply state
      setReplyText('');
      setReplyingTo(null);
      
      // Auto-expand replies for the parent comment
      setExpandedReplies(prev => ({ ...prev, [parentId]: true }));
      
    } catch (err) {
      console.error('Error submitting reply:', err);
      alert('Failed to submit reply');
    } finally {
      setSubmittingReply(false);
    }
  };

  // Delete a specific comment or reply
  const deleteComment = async (reviewId) => {
    if (!confirm('Are you sure you want to delete this comment?')) return;
    
    try {
      const token = localStorage.getItem('livestreamToken');
      await axios.delete(
        `${API}/api/vod/videos/${video.id}/reviews/${reviewId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state - remove the comment or reply
      setReviews(prevReviews => {
        // First check if it's a top-level comment
        const filteredReviews = prevReviews.filter(r => r.id !== reviewId);
        
        // If nothing was removed, it might be a reply
        if (filteredReviews.length === prevReviews.length) {
          return prevReviews.map(review => ({
            ...review,
            replies: (review.replies || []).filter(reply => reply.id !== reviewId),
            reply_count: Math.max(0, (review.reply_count || 0) - ((review.replies || []).some(r => r.id === reviewId) ? 1 : 0))
          }));
        }
        
        return filteredReviews;
      });
      
    } catch (err) {
      console.error('Error deleting comment:', err);
      alert('Failed to delete comment');
    }
  };

  // Toggle replies visibility for a comment
  const toggleReplies = (commentId) => {
    setExpandedReplies(prev => ({ ...prev, [commentId]: !prev[commentId] }));
  };

  const handlePurchase = async () => {
    if (!customer) {
      // Redirect to login
      navigate(getLoginUrl(`/vod/video/${video?.slug || video?.id || videoId}`));
      return;
    }

    // If a free promo code is applied, redeem directly
    if (promoApplied?.is_free) {
      handleRedeemFreePromo();
      return;
    }

    setPurchasing(true);
    try {
      const token = localStorage.getItem('livestreamToken');
      const actualVideoId = video?.id || videoId;
      const payload = {
        video_id: actualVideoId,
        success_url: `${window.location.origin}/vod/payment-success`,
        cancel_url: window.location.href
      };
      // Pass promo code to checkout if applied
      if (promoApplied?.code) {
        payload.promo_code = promoApplied.code;
      }
      const res = await axios.post(
        `${API}/api/vod/payment/purchase-video`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      console.error('Purchase error:', err);
      alert(err.response?.data?.detail || 'Failed to initiate purchase');
    } finally {
      setPurchasing(false);
    }
  };

  // Inline auth for paywall — login or register, then auto-purchase
  const handlePaywallAuth = async (e) => {
    e.preventDefault();
    setPaywallAuthLoading(true);
    setPaywallAuthError('');
    try {
      const endpoint = paywallAuthMode === 'register'
        ? `${API}/api/livestream/auth/register`
        : `${API}/api/livestream/auth/login`;
      const body = paywallAuthMode === 'register'
        ? { email: paywallEmail, password: paywallPassword, full_name: paywallName }
        : { email: paywallEmail, password: paywallPassword };
      const res = await axios.post(endpoint, body);
      if (res.data.token) {
        localStorage.setItem('livestreamToken', res.data.token);
        if (res.data.customer) {
          localStorage.setItem('livestreamCustomer', JSON.stringify(res.data.customer));
          setCustomer(res.data.customer);
        }
        setIsLoggedIn(true);
        setPendingPurchaseAfterAuth(true);
      }
    } catch (err) {
      setPaywallAuthError(err.response?.data?.detail || 'Authentication failed. Please try again.');
    } finally {
      setPaywallAuthLoading(false);
    }
  };

  const handlePaywallGoogleLogin = () => {
    const currentPath = `/vod/video/${video?.slug || video?.id || videoId}`;
    const fromParam = window.location.hostname.includes('ltvvod') ? 'ltvvod' : 'vod';
    let oauthUrl = `${window.location.origin}/api/auth/google/login`;
    oauthUrl += `?redirect_to=${encodeURIComponent(currentPath)}&from=${encodeURIComponent(fromParam)}&auto_purchase=true`;
    // Use target="_top" to break out of any iframe (ltvvod.com domain masking)
    const a = document.createElement('a');
    a.href = oauthUrl;
    a.target = '_top';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Promo code validation
  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoError('');
    try {
      const res = await axios.post(`${API}/api/promo-codes/validate`, {
        code: promoCode.trim(),
        video_id: video?.id || videoId
      });
      setPromoApplied(res.data);
    } catch (err) {
      setPromoError(err.response?.data?.detail || 'Invalid promo code');
      setPromoApplied(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setPromoApplied(null);
    setPromoCode('');
    setPromoError('');
  };

  // Redeem a free promo code (100% discount)
  const handleRedeemFreePromo = async () => {
    if (!promoApplied?.is_free) return;
    
    // Require login for free promo codes
    const token = localStorage.getItem('livestreamToken');
    if (!token || !customer) {
      // Store promo code info so it can be applied after login
      sessionStorage.setItem('pendingPromoCode', JSON.stringify({
        code: promoApplied.code,
        video_id: video?.id || videoId
      }));
      navigate(getLoginUrl(`/vod/video/${video?.slug || video?.id || videoId}`));
      return;
    }
    
    setPurchasing(true);
    try {
      const res = await axios.post(`${API}/api/promo-codes/redeem`, {
        code: promoApplied.code,
        video_id: video?.id || videoId
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.access_granted) {
        setShowPaywall(false);
        setPromoApplied(null);
        setPromoCode('');
        // Re-fetch video data to update access state (no page reload needed)
        await fetchVideo();
      }
    } catch (err) {
      if (err.response?.status === 401) {
        // Token expired or invalid — redirect to login
        sessionStorage.setItem('pendingPromoCode', JSON.stringify({
          code: promoApplied.code,
          video_id: video?.id || videoId
        }));
        navigate(getLoginUrl(`/vod/video/${video?.slug || video?.id || videoId}`));
        return;
      }
      setPromoError(err.response?.data?.detail || 'Failed to redeem promo code');
    } finally {
      setPurchasing(false);
    }
  };

  // Auto-trigger purchase after inline auth or Google OAuth
  useEffect(() => {
    if (pendingPurchaseAfterAuth && isLoggedIn && video?.id) {
      setPendingPurchaseAfterAuth(false);
      setShowPaywall(false);
      handlePurchase();
    }
  }, [pendingPurchaseAfterAuth, isLoggedIn, video?.id]);

  // Track video watch event for analytics
  const trackWatchEvent = async (watchDuration, totalDuration) => {
    if (!video?.id || watchDuration <= 0) return;
    
    // Get the actual current playback position
    const currentPosition = videoPlayerRef.current ? Math.floor(videoPlayerRef.current.currentTime) : 0;
    console.log(`[WatchTracking] trackWatchEvent called: videoId=${video.id}, currentPosition=${currentPosition}s, watchDuration=${Math.floor(watchDuration)}s`);
    
    try {
      const token = localStorage.getItem('livestreamToken');
      
      // Detect device type
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isTablet = /iPad|Android(?!.*Mobile)/i.test(navigator.userAgent);
      const deviceType = isTablet ? 'tablet' : (isMobileDevice ? 'mobile' : 'desktop');
      
      // Get browser name
      const browserInfo = navigator.userAgent;
      let browser = 'Unknown';
      if (browserInfo.includes('Chrome') && !browserInfo.includes('Edg')) browser = 'Chrome';
      else if (browserInfo.includes('Safari') && !browserInfo.includes('Chrome')) browser = 'Safari';
      else if (browserInfo.includes('Firefox')) browser = 'Firefox';
      else if (browserInfo.includes('Edg')) browser = 'Edge';
      else if (browserInfo.includes('Opera') || browserInfo.includes('OPR')) browser = 'Opera';
      
      // Detect Operating System
      let os = 'Unknown';
      if (browserInfo.includes('Win')) os = 'Windows';
      else if (browserInfo.includes('Mac') && !browserInfo.includes('iPhone') && !browserInfo.includes('iPad')) os = 'Mac';
      else if (browserInfo.includes('Linux') && !browserInfo.includes('Android')) os = 'Linux';
      else if (browserInfo.includes('iPhone') || browserInfo.includes('iPad')) os = 'iOS';
      else if (browserInfo.includes('Android')) os = 'Android';
      else if (browserInfo.includes('CrOS')) os = 'Chrome OS';
      
      // Extract UTM parameters from URL
      const urlParams = new URLSearchParams(window.location.search);
      const utmSource = urlParams.get('utm_source');
      const utmMedium = urlParams.get('utm_medium');
      const utmCampaign = urlParams.get('utm_campaign');
      const utmTerm = urlParams.get('utm_term');
      const utmContent = urlParams.get('utm_content');
      
      // Get the INITIAL referrer from when user first arrived on the site
      // Store in sessionStorage so in-site navigation doesn't overwrite it
      const currentHost = window.location.hostname;
      const rawReferrer = document.referrer || '';
      let initialReferrer = sessionStorage.getItem('initial_referrer');
      
      if (initialReferrer === null) {
        // First page load in this session — capture the external referrer
        try {
          const refHost = rawReferrer ? new URL(rawReferrer).hostname : '';
          // If referrer is from our own site, it's a direct visit (or internal navigation)
          const ownDomains = [currentHost, 'ltvproduction.com', 'www.ltvproduction.com', 'ltvvod.com', 'www.ltvvod.com'];
          if (!rawReferrer || ownDomains.some(d => refHost.includes(d))) {
            initialReferrer = 'Direct';
          } else {
            initialReferrer = rawReferrer;
          }
        } catch {
          initialReferrer = rawReferrer || 'Direct';
        }
        sessionStorage.setItem('initial_referrer', initialReferrer);
      }
      
      await axios.post(
        `${API}/api/vod/track-watch`,
        {
          video_id: video.id,
          watch_duration: Math.floor(watchDuration),
          total_duration: Math.floor(totalDuration),
          current_position: currentPosition,
          referrer: initialReferrer,
          device_type: deviceType,
          browser: browser,
          os: os,
          country: null, // Detected server-side via IP
          city: null,
          region: null,
          // UTM Campaign Tracking
          utm_source: utmSource,
          utm_medium: utmMedium,
          utm_campaign: utmCampaign,
          utm_term: utmTerm,
          utm_content: utmContent
        },
        token ? { headers: { Authorization: `Bearer ${token}` } } : {}
      );
      
      console.log('[VODVideoDetail] Watch event tracked:', { watchDuration, totalDuration, os, browser, utmSource, utmCampaign });
    } catch (err) {
      console.error('[VODVideoDetail] Error tracking watch event:', err);
    }
  };
  
  // Real-time viewer heartbeat
  const sendHeartbeat = async () => {
    if (!video?.id || !videoPlayerRef.current) return;
    
    try {
      // Determine playback state from whichever player type is active
      const player = videoPlayerRef.current;
      let isActuallyPlaying = false;
      
      if (typeof player.isPlaying === 'function') {
        // CloudflareStreamPlayer exposes isPlaying()
        isActuallyPlaying = player.isPlaying();
      } else if (typeof player.getState === 'function') {
        // VODPlayer exposes getState()
        isActuallyPlaying = player.getState() === 'playing';
      } else if ('paused' in player) {
        // Native <video> element
        isActuallyPlaying = !player.paused && !player.ended;
      }
      
      await axios.post(`${API}/api/realtime/heartbeat`, {
        video_id: video.id,
        session_id: viewerSessionId.current,
        user_id: customer?.id || null,
        current_time: (typeof player.getCurrentTime === 'function' ? player.getCurrentTime() : player.currentTime) || 0,
        is_playing: isActuallyPlaying,
        is_pip: document.pictureInPictureElement != null
      });
    } catch (err) {
      // Silently ignore heartbeat errors - they should not affect playback
    }
  };
  
  // Start/stop heartbeat based on playback state
  useEffect(() => {
    if (playingInline && video?.id) {
      // Send immediate heartbeat when video starts
      sendHeartbeat();
      
      // Set up interval for every 10 seconds
      heartbeatIntervalRef.current = setInterval(sendHeartbeat, 10000);
      
      // Listen for play/pause events for INSTANT updates
      // Only works on native <video> elements, not on wrapper components
      const videoEl = videoPlayerRef.current;
      const onPlayPause = () => sendHeartbeat();
      const isNativeElement = videoEl && typeof videoEl.addEventListener === 'function' && videoEl.tagName === 'VIDEO';
      if (isNativeElement) {
        videoEl.addEventListener('play', onPlayPause);
        videoEl.addEventListener('pause', onPlayPause);
      }
      
      // Cleanup on unmount or when video stops
      return () => {
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
        }
        if (isNativeElement) {
          videoEl.removeEventListener('play', onPlayPause);
          videoEl.removeEventListener('pause', onPlayPause);
        }
        // Send leave signal
        axios.post(`${API}/api/realtime/leave`, { session_id: viewerSessionId.current }).catch(() => {});
      };
    } else {
      // Stop heartbeat when not playing
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    }
  }, [playingInline, video?.id]);
  
  // Send leave signal on page unload
  useEffect(() => {
    const handleUnload = () => {
      // Use sendBeacon for reliability on page close
      navigator.sendBeacon(
        `${API}/api/realtime/leave`,
        JSON.stringify({ session_id: viewerSessionId.current })
      );
    };
    
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, []);
  
  const handleWatch = () => {
    const videoPrice = video?.price || 0;
    const isFreeVideo = videoPrice <= 0;
    const hasPurchased = video?.has_purchased || video?.access_type === 'purchase';
    
    // If video is paid and user hasn't purchased, show paywall
    if (!isFreeVideo && !hasPurchased && !isSharedLink && !video?.free_preview) {
      setShowPaywall(true);
      return;
    }
    
    // If resume prompt is showing, let the user interact with it first
    if (showResumePrompt && savedProgress && isLoggedIn) {
      return;
    }
    
    // For shared links, free videos, purchased videos, or free preview videos, play inline
    if (isSharedLink || isFreeVideo || hasPurchased || video.free_preview || video.access_type === 'free_preview') {
      // Track video watch for non-logged in users (skip for shared links to not block)
      // Check if popup is enabled before showing it
      if (popupSettingsLoaded && popupSettings.enabled && !isLoggedIn && !isSharedLink) {
        const watchCount = parseInt(localStorage.getItem('vodWatchCount') || '0');
        const newCount = watchCount + 1;
        localStorage.setItem('vodWatchCount', newCount.toString());
        
        // Show signup prompt after watching 2 videos (only if popup is enabled)
        if (newCount >= 2 && !signupPromptShown) {
          setShowSignupPrompt(true);
          setSignupPromptShown(true);
          sessionStorage.setItem('vodSignupPromptShown', 'true');
          return; // Don't start video, show signup first
        }
      }
      // Play video inline instead of opening modal
      setPlayingInline(true);
    } else {
      navigate(`/vod/watch/${videoId || slug}`);
    }
  };
  
  // Stop inline video playback
  const stopInlineVideo = () => {
    // Track watch event before stopping
    if (videoPlayerRef.current && watchTrackingRef.current.totalWatched > 0) {
      const totalDuration = videoPlayerRef.current.duration || video?.duration || 0;
      trackWatchEvent(watchTrackingRef.current.totalWatched, totalDuration);
      watchTrackingRef.current = { lastTracked: 0, totalWatched: 0 }; // Reset tracking
    }
    
    setPlayingInline(false);
    setVideoPlaying(false);
    setInlineAdPlaying(false);
    setAdRequestPending(false);
    preRollAttemptedRef.current = false; // Reset so ad plays again next time
    if (videoPlayerRef.current) {
      videoPlayerRef.current.pause();
      videoPlayerRef.current.currentTime = 0;
    }
    if (videoHlsRef.current) {
      videoHlsRef.current.destroy();
      videoHlsRef.current = null;
    }
    // Reset ads
    resetInlineAds?.();
  };
  
  // Video modal functions (keeping for backwards compat)
  const closeVideoModal = () => {
    setShowVideoModal(false);
    stopInlineVideo();
  };
  
  // Picture-in-Picture handlers - DISABLED since VODPlayer doesn't expose native video element
  useEffect(() => {
    // Skip PiP handling - VODPlayer handles this internally
    return;
    
    const videoEl = videoPlayerRef.current;
    if (!videoEl || typeof videoEl.addEventListener !== 'function') return;
    
    const handleEnterPiP = () => {
      console.log('[VODVideoDetail] Entered Picture-in-Picture mode');
      setIsPiPActive(true);
      
      // If an ad should be playing, pause main content and show ad in original container
      // The ad container stays in the main page, so we need to handle this properly
      if (inlineAdPlaying) {
        // Ad is playing - keep it going in the main container
        console.log('[VODVideoDetail] Ad playing during PiP - continuing ad playback');
      }
    };
    
    const handleLeavePiP = () => {
      console.log('[VODVideoDetail] Left Picture-in-Picture mode');
      setIsPiPActive(false);
    };
    
    videoEl.addEventListener('enterpictureinpicture', handleEnterPiP);
    videoEl.addEventListener('leavepictureinpicture', handleLeavePiP);
    
    return () => {
      videoEl.removeEventListener('enterpictureinpicture', handleEnterPiP);
      videoEl.removeEventListener('leavepictureinpicture', handleLeavePiP);
    };
  }, [videoPlayerRef.current, inlineAdPlaying]);
  
  // Toggle PiP mode
  const togglePictureInPicture = async () => {
    const videoEl = videoPlayerRef.current;
    if (!videoEl) return;
    
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      } else if (document.pictureInPictureEnabled) {
        // If ad is playing, don't allow PiP
        if (inlineAdPlaying) {
          console.log('[VODVideoDetail] Cannot enter PiP during ad playback');
          return;
        }
        await videoEl.requestPictureInPicture();
      }
    } catch (err) {
      console.error('[VODVideoDetail] PiP error:', err);
    }
  };

  const toggleVideoPlay = () => {
    // VODPlayer handles all playback internally
    // This function is only called by legacy code, delegate to VODPlayer
    console.log('[VODVideoDetail] toggleVideoPlay - delegating to VODPlayer');
    if (videoPlayerRef.current) {
      if (typeof videoPlayerRef.current.play === 'function') {
        if (videoPlaying) {
          videoPlayerRef.current.pause();
        } else {
          videoPlayerRef.current.play();
        }
      }
    }
  };
  
  const toggleVideoMute = () => {
    if (videoPlayerRef.current) {
      videoPlayerRef.current.muted = !videoMuted;
      setVideoMuted(!videoMuted);
    }
  };
  
  // Handle tap on video in landscape mode to show/hide controls
  const handleLandscapeVideoTap = (e) => {
    // Only need to check if video is playing inline - CSS handles the landscape detection
    if (!playingInline) return;
    
    // Clear any existing timeout
    if (landscapeControlsTimeoutRef.current) {
      clearTimeout(landscapeControlsTimeoutRef.current);
    }
    
    // Toggle controls visibility
    setShowLandscapeControls(prev => {
      const newValue = !prev;
      
      // If showing controls, auto-hide after 3 seconds
      if (newValue) {
        landscapeControlsTimeoutRef.current = setTimeout(() => {
          setShowLandscapeControls(false);
        }, 3000);
      }
      
      return newValue;
    });
  };
  
  // Reset landscape controls timeout on any control interaction
  const resetLandscapeControlsTimeout = () => {
    if (landscapeControlsTimeoutRef.current) {
      clearTimeout(landscapeControlsTimeoutRef.current);
    }
    
    // Keep controls visible and reset the auto-hide timer
    setShowLandscapeControls(true);
    landscapeControlsTimeoutRef.current = setTimeout(() => {
      setShowLandscapeControls(false);
    }, 3000);
  };
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (landscapeControlsTimeoutRef.current) {
        clearTimeout(landscapeControlsTimeoutRef.current);
      }
    };
  }, []);
  
  // Debug: Log control visibility state
  useEffect(() => {
    if (playingInline) {
      console.log('[Controls Debug] playingInline:', playingInline, 'isLandscape:', isLandscape, 'isMobile:', isMobile, 'showLandscapeControls:', showLandscapeControls);
      console.log('[Controls Debug] Should hide controls:', playingInline && isLandscape && isMobile && !showLandscapeControls);
    }
  }, [playingInline, isLandscape, isMobile, showLandscapeControls]);
  
  const handleVideoTimeUpdate = () => {
    if (videoPlayerRef.current) {
      const current = videoPlayerRef.current.currentTime;
      const duration = videoPlayerRef.current.duration;
      setVideoCurrentTime(current);
      setVideoDuration(duration);
      const progress = (current / duration) * 100;
      setVideoProgress(progress);
      
      // Shared tracking logic
      updateWatchTracking(current, duration);
    }
  };

  // Shared watch tracking logic — called by both inline players and modal player
  const updateWatchTracking = (current, duration) => {
      // Calculate progress percentage
      const progress = duration > 0 ? (current / duration) * 100 : 0;
      
      // Log tracking state for debugging
      if (Math.floor(current) % 10 === 0 && current > 0) {
        console.log(`[WatchTracking] time=${Math.floor(current)}s, videoPlaying=${videoPlaying}, adPlaying=${inlineAdPlaying}, totalWatched=${watchTrackingRef.current.totalWatched.toFixed(1)}s`);
      }
      
      // DEBUG: Update ad debug info
      if (showAdDebug && adSettings) {
        const nextAdBreak = [
          { num: 2, time: adSettings.ad_break_2_time, enabled: adSettings.ad_break_2_enabled },
          { num: 3, time: adSettings.ad_break_3_time, enabled: adSettings.ad_break_3_enabled },
          { num: 4, time: adSettings.ad_break_4_time, enabled: adSettings.ad_break_4_enabled },
          { num: 5, time: adSettings.ad_break_5_time, enabled: adSettings.ad_break_5_enabled },
        ].find(b => b.enabled && b.time > current);
        
        setAdDebugInfo({
          time: current,
          nextAd: nextAdBreak ? `${(nextAdBreak.time/60).toFixed(1)}min (in ${(nextAdBreak.time - current).toFixed(0)}s)` : 'None',
          status: inlineAdPlaying ? 'AD PLAYING' : (adSettings ? 'Ready' : 'Loading...'),
          customAds: hasCustomAdSettings ? 'YES (blocking global)' : 'NO'
        });
      }
      
      // "Next Up" recommendation logic - MUST run before watch tracking (which has early return)
      // Show overlay 20 seconds before the end of the video
      if (duration && duration > 30 && !nextUpDismissedRef.current) {
        const timeRemaining = duration - current;
        
        // Log time remaining for debugging (every second when close to end)
        if (timeRemaining <= 35 && timeRemaining > 0) {
          console.log(`[NextUp] Time: ${current.toFixed(0)}/${duration.toFixed(0)}, remaining: ${timeRemaining.toFixed(0)}s, fetched: ${nextUpFetchedRef.current}, hasRec: ${!!nextVideoRecommendationRef.current}, showing: ${showNextUpOverlayRef.current}`);
        }
        
        // Fetch recommendation when 35 seconds remain (earlier buffer)
        if (timeRemaining <= 35 && !nextUpFetchedRef.current) {
          console.log('[NextUp] Triggering recommendation fetch at', timeRemaining.toFixed(0), 'seconds remaining');
          fetchNextVideoRecommendation(videoRef.current, creatorProfileRef.current, relatedVideosRef.current);
        }
        
        // Show overlay when 20 seconds remain
        if (timeRemaining <= 20 && timeRemaining > 0 && nextVideoRecommendationRef.current && !showNextUpOverlayRef.current) {
          setShowNextUpOverlay(true);
          console.log('[NextUp] SHOWING overlay with', timeRemaining.toFixed(0), 'seconds remaining');
        }
        
        // Hide overlay when video ends or if user seeked back past 25 seconds
        if ((timeRemaining > 25 || timeRemaining <= 0) && showNextUpOverlayRef.current) {
          setShowNextUpOverlay(false);
          console.log('[NextUp] Hiding overlay - time remaining:', timeRemaining.toFixed(0));
        }
      }
      
      // Track watch time for analytics
      if (videoPlaying && !inlineAdPlaying) {
        const now = Date.now();
        
        // Initialize lastTracked on first call or after pause
        if (!watchTrackingRef.current.lastTracked || watchTrackingRef.current.lastTracked === 0) {
          watchTrackingRef.current.lastTracked = now;
          return; // Skip this tick, wait for next one (Next Up logic already ran above)
        }
        
        const timeSinceLastTrack = (now - watchTrackingRef.current.lastTracked) / 1000;
        
        // Only count time if it's a reasonable interval (0.1-5 seconds)
        // timeupdate fires every ~250ms in most browsers, so threshold must be low enough
        if (timeSinceLastTrack >= 0.1 && timeSinceLastTrack <= 5) {
          watchTrackingRef.current.totalWatched += timeSinceLastTrack;
        } else if (timeSinceLastTrack > 5) {
          // Video was likely paused or seeked, just reset the timestamp
          console.log(`[WatchTracking] Gap detected (${timeSinceLastTrack.toFixed(2)}s), resetting tracker`);
        }
        
        watchTrackingRef.current.lastTracked = now;
        
        // Send tracking event every 30 seconds during playback
        if (watchTrackingRef.current.totalWatched >= 30 && 
            Math.floor(watchTrackingRef.current.totalWatched) % 30 < 1) {
          trackWatchEvent(watchTrackingRef.current.totalWatched, duration);
        }
        
        // Record view after 30 seconds of actual watch time (YouTube-style)
        if (watchTrackingRef.current.totalWatched >= 30 && !watchTrackingRef.current.viewCounted) {
          watchTrackingRef.current.viewCounted = true;
          axios.post(`${API}/api/vod/videos/${video?.id}/view`, {}, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          }).then(res => {
            if (res.data?.counted) {
              console.log('[ViewCount] View recorded after 30s watch time');
            } else {
              console.log('[ViewCount] Duplicate view — not counted');
            }
          }).catch(() => {});
        }
      } else {
        // Video is paused or ad is playing, reset the lastTracked so we don't count pause time
        watchTrackingRef.current.lastTracked = 0;
      }
      
      // Show signup prompt based on admin settings (once per session)
      // Only show if: settings loaded, popup enabled, user not logged in, and not already shown
      if (popupSettingsLoaded && popupSettings.enabled && !isLoggedIn && !signupPromptShown) {
        let shouldShowPopup = false;
        
        if (popupSettings.trigger_type === 'on_play') {
          // Trigger at the beginning when user presses play (within first 2 seconds)
          shouldShowPopup = current >= 0.5 && current < 2;
        } else if (popupSettings.trigger_type === 'percentage') {
          // Trigger at X% of video
          const triggerPercent = popupSettings.trigger_value || 50;
          shouldShowPopup = progress >= triggerPercent && progress < (triggerPercent + 2);
        } else if (popupSettings.trigger_type === 'seconds') {
          // Trigger after X seconds
          const triggerSeconds = popupSettings.trigger_value || 30;
          shouldShowPopup = current >= triggerSeconds && current < (triggerSeconds + 2);
        }
        
        if (shouldShowPopup) {
          setShowSignupPrompt(true);
          setSignupPromptShown(true);
          sessionStorage.setItem('vodSignupPromptShown', 'true');
          // Pause the video when showing prompt
          videoPlayerRef.current.pause();
          setVideoPlaying(false);
        }
      }
  };
  
  const handleVideoSeek = (e) => {
    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    const percentage = clickX / width;
    if (videoPlayerRef.current && videoDuration) {
      videoPlayerRef.current.currentTime = percentage * videoDuration;
    }
  };
  
  const toggleFullscreen = async () => {
    // On desktop: Use native Fullscreen API for true fullscreen (hides URL bar)
    // On mobile: Use CSS-based fullscreen to ensure ads display properly
    
    const isDesktop = !isMobile;
    
    if (isFullscreen) {
      // EXIT FULLSCREEN
      console.log('[Fullscreen] Exiting fullscreen');
      setIsFullscreen(false);
      
      // Exit native fullscreen if active
      if (document.fullscreenElement) {
        try {
          await document.exitFullscreen();
        } catch (e) {
          console.log('[Fullscreen] Exit error:', e);
        }
      }
      
      // Exit webkit fullscreen (Safari)
      if (document.webkitFullscreenElement) {
        try {
          document.webkitExitFullscreen();
        } catch (e) {
          console.log('[Fullscreen] Webkit exit error:', e);
        }
      }
      
      // Unlock screen orientation if it was locked
      if (screen.orientation && screen.orientation.unlock) {
        try {
          screen.orientation.unlock();
        } catch (e) {
          // Ignore - may not be supported
        }
      }
    } else {
      // ENTER FULLSCREEN
      setIsFullscreen(true);
      
      if (isDesktop) {
        // Desktop: Use native fullscreen on the video container (includes ad container)
        const container = videoContainerRef.current;
        if (container) {
          try {
            if (container.requestFullscreen) {
              await container.requestFullscreen();
              console.log('[Fullscreen] Entered native fullscreen (desktop)');
            } else if (container.webkitRequestFullscreen) {
              container.webkitRequestFullscreen();
              console.log('[Fullscreen] Entered webkit fullscreen (desktop Safari)');
            }
          } catch (e) {
            console.log('[Fullscreen] Native fullscreen error:', e);
            // Fallback to CSS fullscreen
          }
        }
      } else {
        // Mobile: CSS-based fullscreen (allows ad overlays)
        console.log('[Fullscreen] Entering CSS-based fullscreen (mobile)');
        
        // Try to lock orientation to landscape for better experience
        if (screen.orientation && screen.orientation.lock) {
          screen.orientation.lock('landscape').catch(() => {
            // Orientation lock not supported or not allowed
          });
        }
      }
    }
  };
  
  // Change video quality for modal player
  const changeModalQuality = (quality) => {
    if (!videoPlayerRef.current || !video) return;
    
    const currentTime = videoPlayerRef.current.currentTime;
    const wasPlaying = !videoPlayerRef.current.paused;
    
    // Disable auto mode when manually selecting
    setAutoQualityEnabled(false);
    setQualityDropped(false);
    
    // Determine the new URL based on quality
    const newUrl = quality === '4k' ? video.r2_url_4k : video.r2_url_1080p;
    
    if (newUrl) {
      videoPlayerRef.current.src = newUrl;
      videoPlayerRef.current.currentTime = currentTime;
      setModalCurrentQuality(quality);
      
      if (wasPlaying) {
        videoPlayerRef.current.play().catch(console.error);
      }
    }
    
    setShowQualityMenu(false);
  };
  
  // Handle buffering for auto quality switching
  const handleModalBuffering = () => {
    if (!autoQualityEnabled || qualityDropped) return;
    if (!video?.r2_url_4k || !video?.r2_url_1080p) return;
    if (modalCurrentQuality !== '4k') return;
    
    const now = Date.now();
    // Only count buffering events that are at least 2 seconds apart
    if (now - lastBufferCheckRef.current > 2000) {
      lastBufferCheckRef.current = now;
      setBufferingCount(prev => {
        const newCount = prev + 1;
        // After 3 buffering events, drop to 1080p
        if (newCount >= 3) {
          // Auto-switch to 1080p
          const currentTime = videoPlayerRef.current?.currentTime || 0;
          const wasPlaying = videoPlayerRef.current && !videoPlayerRef.current.paused;
          
          if (video.r2_url_1080p && videoPlayerRef.current) {
            videoPlayerRef.current.src = video.r2_url_1080p;
            videoPlayerRef.current.currentTime = currentTime;
            setModalCurrentQuality('1080p');
            setQualityDropped(true);
            setShowQualityNotification(true);
            
            // Hide notification after 5 seconds
            setTimeout(() => setShowQualityNotification(false), 5000);
            
            if (wasPlaying) {
              videoPlayerRef.current.play().catch(console.error);
            }
          }
          return 0;
        }
        return newCount;
      });
    }
  };
  
  // Handle AirPlay cast
  const handleCast = () => {
    const video = videoPlayerRef.current;
    if (!video) return;
    
    // Check for Safari AirPlay
    if (video.webkitShowPlaybackTargetPicker) {
      video.webkitShowPlaybackTargetPicker();
      return;
    }
    
    // Check for Chrome Cast API
    if (window.chrome && window.chrome.cast) {
      // Chrome Cast available
      const castContext = window.cast?.framework?.CastContext?.getInstance();
      if (castContext) {
        castContext.requestSession().catch(console.error);
        return;
      }
    }
    
    // Fallback - show instructions
    alert('To cast this video:\n\n• Safari: Use AirPlay from the video controls or menu bar\n• Chrome: Click the Cast icon in your browser toolbar\n• Mobile: Use screen mirroring from Control Center (iOS) or Quick Settings (Android)');
  };
  
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Initialize video player when inline mode is activated - DISABLED since VODPlayer handles this
  useEffect(() => {
    if (!playingInline || !videoPlayerRef.current) return;
    
    // VODPlayer handles all video initialization internally
    console.log('[VODVideoDetail] VODPlayer active - skipping legacy init');
    return;
    
    // Reset pre-roll flag when inline player opens (but NOT preRollCompletedRef)
    preRollAttemptedRef.current = false;
    setVideoPlaying(false);
    
    // For R2 URLs or direct video_url, the video element handles it directly via src attribute
    if (video?.r2_url_4k || video?.r2_url_1080p || video?.video_url) {
      // Set initial quality based on what's available
      if (video.r2_url_4k) {
        setModalCurrentQuality('4k');
      } else if (video.r2_url_1080p) {
        setModalCurrentQuality('1080p');
      }
      
      // Auto-play after a short delay to let the video element initialize
      setTimeout(() => {
        if (videoPlayerRef.current && !isRestoringContentRef.current && !preRollCompletedRef.current) {
          console.log('[VODVideoDetail] Auto-playing R2/direct video inline');
          // Trigger the play through toggleVideoPlay to handle ads properly
          toggleVideoPlay();
        }
      }, 300);
      return;
    }
    
    // For Cloudflare Stream HLS videos
    if (video?.cloudflare_video_id) {
      const videoUrl = `https://customer-${CLOUDFLARE_CUSTOMER_CODE}.cloudflarestream.com/${video.cloudflare_video_id}/manifest/video.m3u8`;
      
      if (Hls.isSupported()) {
        const hls = new Hls();
        hls.loadSource(videoUrl);
        hls.attachMedia(videoPlayerRef.current);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          // Auto-play after manifest is loaded
          console.log('[VODVideoDetail] HLS manifest parsed, auto-playing inline');
          setTimeout(() => {
            if (!isRestoringContentRef.current && !preRollCompletedRef.current) {
              toggleVideoPlay();
            }
          }, 100);
        });
        videoHlsRef.current = hls;
      } else if (videoPlayerRef.current.canPlayType('application/vnd.apple.mpegurl')) {
        videoPlayerRef.current.src = videoUrl;
        // Auto-play for native HLS
        setTimeout(() => {
          if (!isRestoringContentRef.current && !preRollCompletedRef.current) {
            console.log('[VODVideoDetail] Native HLS ready, auto-playing inline');
            toggleVideoPlay();
          }
        }, 300);
      }
    }
    
    return () => {
      if (videoHlsRef.current) {
        videoHlsRef.current.destroy();
        videoHlsRef.current = null;
      }
    };
  }, [playingInline, video?.cloudflare_video_id, video?.r2_url_4k, video?.r2_url_1080p, video?.video_url]);

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const hrs = Math.floor(mins / 60);
    if (hrs > 0) return `${hrs}h ${mins % 60}m`;
    return `${mins} min`;
  };

  const formatViews = (count) => {
    if (!count) return '0';
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count.toString();
  };

  // Initialize trailer player
  useEffect(() => {
    if (showTrailer && video?.trailer_url && trailerRef.current) {
      const trailerHlsUrl = `https://customer-${CLOUDFLARE_CUSTOMER_CODE}.cloudflarestream.com/${video.trailer_url}/manifest/video.m3u8`;
      
      if (Hls.isSupported()) {
        hlsRef.current = new Hls();
        hlsRef.current.loadSource(trailerHlsUrl);
        hlsRef.current.attachMedia(trailerRef.current);
        hlsRef.current.on(Hls.Events.MANIFEST_PARSED, () => {
          trailerRef.current?.play();
          setIsPlaying(true);
        });
      } else if (trailerRef.current.canPlayType('application/vnd.apple.mpegurl')) {
        trailerRef.current.src = trailerHlsUrl;
        trailerRef.current.play();
        setIsPlaying(true);
      }
    }
    
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [showTrailer, video?.trailer_url]);

  const togglePlayPause = () => {
    if (trailerRef.current) {
      if (isPlaying) {
        trailerRef.current.pause();
      } else {
        trailerRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (trailerRef.current) {
      trailerRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const closeTrailer = () => {
    if (trailerRef.current) {
      trailerRef.current.pause();
    }
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    setShowTrailer(false);
    setIsPlaying(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!video) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-white">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Video not found</h2>
          <Button onClick={() => navigate('/vod')} className="bg-purple-600">
            Back to VOD
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* SEO Meta Tags & Social Sharing (WhatsApp, Instagram, Snapchat, TikTok, Facebook, Twitter) */}
      <Helmet>
        <title>{video.seo_title || video.title} | LTV VOD</title>
        <meta name="description" content={video.seo_description || video.description?.substring(0, 160)} />
        <meta name="keywords" content={video.seo_keywords || ''} />
        <link rel="canonical" href={`${SITE_URL}/vod/v/${video.slug || video.id}`} />
        
        {/* Open Graph Tags (Facebook, WhatsApp, Instagram, TikTok, Snapchat) */}
        <meta property="og:type" content="video.other" />
        <meta property="og:title" content={video.seo_title || video.title} />
        <meta property="og:description" content={video.seo_description || video.description?.substring(0, 160)} />
        {/* Use social_image_url if available (1200x630), otherwise thumbnail — route through compression proxy */}
        {(() => {
          const raw = video.social_image_url || video.thumbnail_url;
          const proxied = raw ? `https://ltvproduction.com/api/seo/og-image-proxy?url=${encodeURIComponent(raw)}` : 'https://ltvproduction.com/api/seo/og-image';
          return (
            <>
              <meta property="og:image" content={proxied} />
              <meta property="og:image:width" content="1200" />
              <meta property="og:image:height" content="630" />
              <meta property="og:image:alt" content={video.title} />
            </>
          );
        })()}
        <meta property="og:url" content={`${SITE_URL}/vod/v/${video.slug || video.id}`} />
        <meta property="og:site_name" content="LTV Production" />
        <meta property="og:locale" content="en_GB" />
        <meta property="og:video:duration" content={video.duration || 0} />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@ltvproduction" />
        <meta name="twitter:title" content={video.seo_title || video.title} />
        <meta name="twitter:description" content={video.seo_description || video.description?.substring(0, 160)} />
        {(() => {
          const raw = video.social_image_url || video.thumbnail_url;
          const proxied = raw ? `https://ltvproduction.com/api/seo/og-image-proxy?url=${encodeURIComponent(raw)}` : 'https://ltvproduction.com/api/seo/og-image';
          return (
            <>
              <meta name="twitter:image" content={proxied} />
              <meta name="twitter:image:alt" content={video.title} />
              <meta property="og:image:secure_url" content={proxied} />
              <meta property="og:image:type" content="image/jpeg" />
            </>
          );
        })()}
        
        {/* VideoObject JSON-LD Schema for Google Video Search */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "VideoObject",
            "name": video.seo_title || video.title,
            "description": video.seo_description || video.description,
            "thumbnailUrl": video.social_image_url || video.thumbnail_url,
            "uploadDate": video.created_at || new Date().toISOString(),
            "duration": video.duration ? `PT${Math.floor(video.duration / 60)}M${video.duration % 60}S` : undefined,
            "contentUrl": `${SITE_URL}/vod/watch/${video.id}`,
            "embedUrl": `${SITE_URL}/vod/watch/${video.id}`,
            "interactionStatistic": {
              "@type": "InteractionCounter",
              "interactionType": { "@type": "WatchAction" },
              "userInteractionCount": video.view_count || video.views || 0
            },
            "publisher": {
              "@type": "Organization",
              "name": "LTV Production",
              "logo": {
                "@type": "ImageObject",
                "url": `${SITE_URL}/ltv-icon.png`
              }
            },
            ...(video.seo_keywords ? { "keywords": video.seo_keywords } : {})
          })}
        </script>
      </Helmet>

      {/* Header - Hide in fullscreen mode OR when in landscape with video playing */}
      {/* Using both JS state AND CSS media query for reliability */}
      <header 
        className={`sticky top-0 z-50 bg-black/90 backdrop-blur-sm border-b border-white/10 ${
          playingInline ? 'vod-landscape-hide' : ''
        } ${(isFullscreen || (isLandscape && playingInline && isMobile)) ? 'hidden' : ''}`}
      >
        <style>{`
          @media screen and (orientation: landscape) and (max-width: 926px) {
            .vod-landscape-hide {
              display: none !important;
            }
          }
          @media screen and (orientation: landscape) and (max-height: 600px) {
            .vod-landscape-hide {
              display: none !important;
            }
          }
        `}</style>
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between">
          {/* Left - Back button and Logo */}
          <div className="flex items-center gap-2 sm:gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/vod')}
              className="text-gray-400 hover:text-white px-2"
            >
              <ArrowLeft className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Back</span>
            </Button>
            
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/vod')}>
              <img 
                src="/ltv-icon.png" 
                alt="LTV Logo" 
                className="w-7 h-7 sm:w-8 sm:h-8 object-contain brightness-0 invert"
              />
              <span className="font-bold text-sm sm:text-base">LTV VOD</span>
            </div>
          </div>
          
          {/* Right - Auth buttons */}
          <div className="flex items-center gap-1 sm:gap-2">
            {/* Go Premium Button - Always visible */}
            <Button
              size="sm"
              onClick={() => navigate('/vod/membership')}
              className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 text-yellow-300 hover:from-yellow-500/30 hover:to-orange-500/30 border border-yellow-500/30 px-2 sm:px-3"
            >
              <Crown className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">Go Premium</span>
            </Button>
            
            {isLoggedIn ? (
              <>
                {/* My Watchlist */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/vod/account?tab=watchlist')}
                  className="text-gray-400 hover:text-white px-2"
                >
                  <Bookmark className="h-4 w-4 sm:mr-1" />
                  <span className="hidden md:inline">Watchlist</span>
                </Button>
                
                {/* My Account */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/vod/account')}
                  className="text-gray-400 hover:text-white px-2"
                >
                  <User className="h-4 w-4 sm:mr-1" />
                  <span className="hidden md:inline">Account</span>
                </Button>
                
                {/* Sign Out */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    localStorage.removeItem('vodToken');
                    localStorage.removeItem('vodCustomer');
                    window.location.reload();
                  }}
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10 px-2 sm:px-3"
                >
                  <LogIn className="h-4 w-4 sm:mr-1 rotate-180" />
                  <span className="hidden sm:inline">Sign Out</span>
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                onClick={() => {
                  // Save current video position if video is playing
                  const currentTime = videoPlayerRef.current?.currentTime || 0;
                  const videoSlug = video?.slug || video?.id || videoId;
                  
                  if (currentTime > 0) {
                    // Store in sessionStorage for after login redirect
                    sessionStorage.setItem('vodResumeVideo', JSON.stringify({
                      videoSlug,
                      timestamp: currentTime,
                      shouldAutoPlay: true
                    }));
                    navigate(getLoginUrl(`/vod/video/${videoSlug}?autoplay=true&t=${Math.floor(currentTime)}`));
                  } else {
                    navigate(getLoginUrl(`/vod/video/${videoSlug}`));
                  }
                }}
                className="bg-purple-600 hover:bg-purple-500"
              >
                <LogIn className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Sign In</span>
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Trailer Modal */}
      {showTrailer && video?.trailer_url && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center">
          <div className="relative w-full max-w-5xl mx-4">
            {/* Close button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={closeTrailer}
              className="absolute -top-12 right-0 text-white hover:bg-white/20"
            >
              <X className="h-6 w-6" />
            </Button>
            
            {/* Video container */}
            <div className="relative aspect-video rounded-2xl overflow-hidden bg-black">
              <video
                ref={trailerRef}
                className="w-full h-full object-contain"
                muted={isMuted}
                playsInline
                controls={false}
                controlsList="nodownload noplaybackrate"
                onContextMenu={(e) => e.preventDefault()}
                onEnded={() => setIsPlaying(false)}
              />
              
              {/* Controls overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={togglePlayPause}
                      className="text-white hover:bg-white/20"
                    >
                      {isPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={toggleMute}
                      className="text-white hover:bg-white/20"
                    >
                      {isMuted ? <VolumeX className="h-6 w-6" /> : <Volume2 className="h-6 w-6" />}
                    </Button>
                  </div>
                  <span className="text-white text-sm font-medium">Trailer Preview</span>
                </div>
              </div>
            </div>
            
            {/* Title below video */}
            <div className="mt-4 text-center">
              <h3 className="text-xl font-bold text-white">{video.title} - Trailer</h3>
              <p className="text-gray-400 mt-1">Watch the full video by purchasing or subscribing</p>
            </div>
          </div>
        </div>
      )}

      {/* Video Player Modal (for free videos) */}
      {showVideoModal && (video?.cloudflare_video_id || video?.r2_url_4k || video?.r2_url_1080p || video?.video_url) && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center">
          <div className="relative w-full max-w-6xl mx-4">
            {/* Close button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={closeVideoModal}
              className="absolute -top-12 right-0 text-white hover:bg-white/20 z-10"
            >
              <X className="h-6 w-6" />
              <span className="ml-2">Close</span>
            </Button>
            
            {/* Video container */}
            <div className="relative aspect-video rounded-2xl overflow-hidden bg-black group">
              {/* Ad Container - overlays video during ads */}
              <div 
                ref={modalAdContainerRef}
                className={`absolute inset-0 z-30 ${modalAdPlaying ? '' : 'pointer-events-none'}`}
                style={{ display: modalAdPlaying ? 'block' : 'none' }}
              />
              
              {/* Ad Playing Indicator */}
              {modalAdPlaying && (
                <div className="absolute top-4 left-4 right-4 z-[200] flex items-center justify-between pointer-events-auto">
                  <div className="bg-yellow-500 text-black px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-2">
                    <span className="w-2 h-2 bg-black rounded-full animate-pulse"></span>
                    Advertisement
                  </div>
                  {/* Ad Controls - Play/Pause and Fullscreen */}
                  <div className="flex items-center gap-2">
                    {/* Play/Pause Button for Ad */}
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        console.log('[VODVideoDetail] Top Ad play/pause clicked');
                        toggleAdPlayPause();
                      }}
                      className="bg-black/60 backdrop-blur-sm text-white hover:bg-black/80 p-2 rounded-full transition-colors cursor-pointer"
                      title={adPaused ? "Resume Ad" : "Pause Ad"}
                    >
                      {adPaused ? <Play className="h-5 w-5" /> : <Pause className="h-5 w-5" />}
                    </button>
                    {/* Fullscreen button */}
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFullscreen();
                      }}
                      className="bg-black/60 backdrop-blur-sm text-white hover:bg-black/80 p-2 rounded-full transition-colors cursor-pointer"
                      title="Fullscreen"
                    >
                      <Maximize className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              )}
              
              {/* BOTTOM CONTROL BAR DURING ADS */}
              {modalAdPlaying && (
                <div className="absolute bottom-0 left-0 right-0 z-[200] bg-gradient-to-t from-black/90 to-transparent p-4 pointer-events-auto">
                  <div className="flex items-center gap-4">
                    {/* Play/Pause Ad */}
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        console.log('[VODVideoDetail] Bottom Ad play/pause clicked');
                        toggleAdPlayPause();
                      }}
                      className="text-white hover:text-purple-400 transition-colors cursor-pointer"
                      title={adPaused ? "Resume Ad" : "Pause Ad"}
                    >
                      {adPaused ? <Play className="h-6 w-6" /> : <Pause className="h-6 w-6" />}
                    </button>
                    
                    {/* Volume */}
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setVideoMuted(!videoMuted);
                      }}
                      className="text-white hover:text-purple-400 transition-colors cursor-pointer"
                    >
                      {videoMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
                    </button>
                    
                    {/* Ad Label */}
                    <span className="text-sm text-yellow-400 font-medium">
                      Ad Playing
                    </span>
                    
                    {/* Spacer */}
                    <div className="flex-1" />
                    
                    {/* Fullscreen */}
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFullscreen();
                      }}
                      className="text-white hover:text-purple-400 transition-colors cursor-pointer"
                      title="Fullscreen"
                    >
                      <Maximize className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              )}
              
              {/* Custom Ad Skip Button */}
              {modalAdPlaying && adType === 'custom' && (
                <div className="absolute bottom-20 right-4 z-[200] pointer-events-auto">
                  {showSkipButton ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        skipCustomAd();
                      }}
                      className="bg-white/90 text-black px-4 py-2 rounded font-semibold hover:bg-white transition-colors cursor-pointer flex items-center gap-2"
                    >
                      Skip Ad →
                    </button>
                  ) : (
                    <div className="bg-black/80 text-white px-4 py-2 rounded-lg">
                      Skip in {skipCountdown}s
                    </div>
                  )}
                </div>
              )}
              
              {/* Custom Ad Call-to-Action Button */}
              {modalAdPlaying && adType === 'custom' && customAd?.cta_enabled && customAd?.click_url && (
                <div className="absolute bottom-20 left-4 z-[200] pointer-events-auto">
                  <a
                    href={customAd.click_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      // Track click
                      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/vod/admin/ads/track-click/${customAd.id}`, { method: 'POST' }).catch(() => {});
                    }}
                    className="bg-purple-600 hover:bg-purple-500 text-white px-5 py-3 rounded-lg font-medium flex items-center gap-2 transition-colors shadow-lg animate-pulse hover:animate-none"
                  >
                    {customAd.cta_text || 'Learn More'}
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              )}
              
              <video
                ref={videoPlayerRef}
                className="w-full h-full object-contain"
                muted={videoMuted}
                playsInline
                controls={false}
                controlsList="nodownload noplaybackrate"
                src={video?.r2_url_4k || video?.r2_url_1080p || video?.video_url || ''}
                onContextMenu={(e) => e.preventDefault()}
                onTimeUpdate={handleVideoTimeUpdate}
                onEnded={() => {
                  // Track final watch time when video ends
                  if (watchTrackingRef.current.totalWatched > 0) {
                    const totalDuration = videoPlayerRef.current?.duration || video?.duration || 0;
                    trackWatchEvent(watchTrackingRef.current.totalWatched, totalDuration);
                    console.log('[VODVideoDetail] Video ended, tracked total watch time:', watchTrackingRef.current.totalWatched);
                    watchTrackingRef.current = { lastTracked: 0, totalWatched: 0 };
                  }
                  setVideoPlaying(false);
                }}
                onClick={() => !modalAdPlaying && toggleVideoPlay()}
                onWaiting={handleModalBuffering}
              />
              
              {/* Quality Indicator Badge (always visible in corner) */}
              {(video?.r2_url_4k || video?.r2_url_1080p) && (
                <div className="absolute top-4 right-4 z-10">
                  <Badge className={`${modalCurrentQuality === '4k' ? 'bg-amber-500 text-black' : 'bg-purple-600 text-white'} font-bold`}>
                    {modalCurrentQuality === '4k' ? '4K' : '1080p'}
                    {autoQualityEnabled && !qualityDropped && <span className="ml-1 text-xs opacity-75">(Auto)</span>}
                  </Badge>
                </div>
              )}
              
              {/* Auto Quality Drop Notification */}
              {showQualityNotification && (
                <div className="absolute top-16 left-1/2 -translate-x-1/2 z-20 animate-in fade-in slide-in-from-top duration-300">
                  <div className="bg-black/90 backdrop-blur border border-amber-500/30 rounded-lg px-4 py-2 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
                      <Settings className="h-4 w-4 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">Quality adjusted to 1080p</p>
                      <p className="text-gray-400 text-xs">Optimized for your connection speed</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Play/Pause overlay (shown when paused and not during ad or ad loading) */}
              {!videoPlaying && !modalAdPlaying && !adRequestPending && (
                <div 
                  className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer z-20"
                  onClick={toggleVideoPlay}
                >
                  <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                    <Play className="h-10 w-10 text-white ml-1" fill="white" />
                  </div>
                </div>
              )}
              
              {/* Loading indicator when ad is being requested */}
              {adRequestPending && !modalAdPlaying && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-20">
                  <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin" />
                </div>
              )}
              
              {/* Controls overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/90 via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                {/* Progress bar */}
                <div 
                  className="w-full h-1 bg-white/30 rounded-full mb-3 cursor-pointer"
                  onClick={handleVideoSeek}
                >
                  <div 
                    className="h-full bg-purple-500 rounded-full relative"
                    style={{ width: `${videoProgress}%` }}
                  >
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-lg" />
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={toggleVideoPlay}
                      className="text-white hover:bg-white/20"
                    >
                      {videoPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={toggleVideoMute}
                      className="text-white hover:bg-white/20"
                    >
                      {videoMuted ? <VolumeX className="h-6 w-6" /> : <Volume2 className="h-6 w-6" />}
                    </Button>
                    <span className="text-white text-sm">
                      {formatTime(videoCurrentTime)} / {formatTime(videoDuration)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Quality Badge Indicator - Always visible */}
                    {(video?.r2_url_4k || video?.r2_url_1080p || video?.video_url || video?.cloudflare_video_id) && (
                      <div className={`px-2 py-1 rounded text-xs font-bold ${
                        video?.cloudflare_video_id
                          ? 'bg-blue-500/30 text-blue-300 border border-blue-500/40'
                          : (modalCurrentQuality === '4k' || (video?.r2_url_4k && !video?.r2_url_1080p))
                            ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-black' 
                            : 'bg-white/20 text-white'
                      }`} data-testid="quality-badge">
                        {video?.cloudflare_video_id 
                          ? 'Auto HD'
                          : (modalCurrentQuality === '4k' || (video?.r2_url_4k && !video?.r2_url_1080p)) ? '4K' : '1080p'}
                      </div>
                    )}
                    
                    {/* Quality Selector */}
                    {(video?.r2_url_4k && video?.r2_url_1080p) && (
                      <div className="relative">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowQualityMenu(!showQualityMenu)}
                          className="text-white hover:bg-white/20 flex items-center gap-1"
                        >
                          <Settings className="h-5 w-5" />
                          <span className={`text-xs font-bold ${modalCurrentQuality === '4k' ? 'text-amber-400' : ''}`}>
                            {modalCurrentQuality === '4k' ? '4K' : '1080p'}
                          </span>
                        </Button>
                        
                        {/* Quality Menu */}
                        {showQualityMenu && (
                          <div className="absolute bottom-full right-0 mb-2 bg-black/95 rounded-lg border border-white/20 overflow-hidden min-w-[140px]">
                            {/* Auto option */}
                            <button
                              onClick={() => {
                                setAutoQualityEnabled(true);
                                setQualityDropped(false);
                                // Reset to 4K
                                if (modalCurrentQuality === '1080p' && video.r2_url_4k && videoPlayerRef.current) {
                                  const currentTime = videoPlayerRef.current.currentTime;
                                  const wasPlaying = !videoPlayerRef.current.paused;
                                  videoPlayerRef.current.src = video.r2_url_4k;
                                  videoPlayerRef.current.currentTime = currentTime;
                                  setModalCurrentQuality('4k');
                                  if (wasPlaying) videoPlayerRef.current.play().catch(() => {});
                                }
                                setShowQualityMenu(false);
                              }}
                              className={`w-full px-4 py-2 text-left text-sm hover:bg-white/10 flex items-center justify-between border-b border-white/10 ${
                                autoQualityEnabled ? 'text-green-400' : 'text-white'
                              }`}
                            >
                              <span>Auto (Smart)</span>
                              {autoQualityEnabled && <Check className="h-4 w-4" />}
                            </button>
                            
                            {/* 4K option */}
                            <button
                              onClick={() => changeModalQuality('4k')}
                              className={`w-full px-4 py-2 text-left text-sm hover:bg-white/10 flex items-center justify-between ${
                                !autoQualityEnabled && modalCurrentQuality === '4k' ? 'text-amber-400' : 'text-white'
                              }`}
                            >
                              <span className="flex items-center gap-2">
                                <span className="px-1.5 py-0.5 bg-amber-500 text-black text-xs font-bold rounded">4K</span>
                                2160p
                              </span>
                              {!autoQualityEnabled && modalCurrentQuality === '4k' && <Check className="h-4 w-4" />}
                            </button>
                            
                            {/* 1080p option */}
                            <button
                              onClick={() => changeModalQuality('1080p')}
                              className={`w-full px-4 py-2 text-left text-sm hover:bg-white/10 flex items-center justify-between ${
                                !autoQualityEnabled && modalCurrentQuality === '1080p' ? 'text-purple-400' : 'text-white'
                              }`}
                            >
                              <span>1080p HD</span>
                              {!autoQualityEnabled && modalCurrentQuality === '1080p' && <Check className="h-4 w-4" />}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Cast/AirPlay Button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleCast}
                      className="text-white hover:bg-white/20"
                      title="Cast to TV (AirPlay/Chromecast)"
                    >
                      <Cast className="h-5 w-5" />
                    </Button>
                    
                    {/* Fullscreen Button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={toggleFullscreen}
                      className="text-white hover:bg-white/20"
                    >
                      <Maximize className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Title below video - Modern Cinematic Style */}
            <div className="mt-4 pt-3">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                <div className="flex-1 min-w-0 w-full">
                  {/* Category Badge */}
                  {video.category?.name && (
                    <div className="inline-flex items-center gap-2 mb-2">
                      <span className="px-2.5 py-0.5 bg-purple-500/20 border border-purple-500/30 rounded-full text-purple-300 text-xs font-medium uppercase tracking-wider">
                        {video.category.name}
                      </span>
                      {video.has_4k && (
                        <span className="px-2 py-0.5 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded text-amber-300 text-xs font-bold">
                          4K
                        </span>
                      )}
                    </div>
                  )}
                  
                  {/* Title - Full width on mobile */}
                  <h3 className="text-xl sm:text-2xl font-bold text-white leading-tight tracking-tight mb-1.5">
                    {video.title}
                  </h3>
                  
                  {/* Meta Info - Full width on mobile */}
                  <div className="flex flex-wrap items-center gap-2.5 text-sm">
                    {video.duration > 0 && (
                      <span className="flex items-center gap-1.5 text-gray-400">
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"/>
                          <polyline points="12 6 12 12 16 14"/>
                        </svg>
                        {formatDuration(video.duration)}
                      </span>
                    )}
                    <span className="flex items-center gap-1.5 text-gray-400">
                      <Eye className="h-3.5 w-3.5 text-purple-400" />
                      <span className="text-white/80">{formatViews(video.view_count || 0)}</span>
                      <span className="text-gray-500">views</span>
                    </span>
                  </div>
                </div>
                
                {/* Action Buttons - Full width on mobile, right-aligned on desktop */}
                <div className="flex items-center gap-2 w-full sm:w-auto justify-start sm:justify-end">
                  {/* Comment Button */}
                  <button
                    onClick={() => {
                      if (!customer) {
                        navigate(getLoginUrl(`/vod/video/${video?.slug || video?.id || videoId}`));
                      } else if (isMobile) {
                        openMobileCommentModal();
                      } else {
                        setShowQuickComment(true);
                      }
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/15 backdrop-blur-sm rounded-full text-white text-sm font-medium transition-all hover:scale-105 border border-white/10"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Comment
                  </button>
                  
                  {/* Share Button - Pill Style */}
                  <button
                    onClick={() => setShowShareModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/15 backdrop-blur-sm rounded-full text-white text-sm font-medium transition-all hover:scale-105 border border-white/10"
                  >
                    <Share2 className="h-4 w-4" />
                    Share
                  </button>
                </div>
              </div>
            </div>
            
            {/* Quick Comment Popup - Desktop only (Slides in from bottom) */}
            {showQuickComment && !isMobile && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/95 to-transparent pt-8 pb-4 px-4 animate-in slide-in-from-bottom duration-300">
                {quickCommentSuccess ? (
                  /* Success State */
                  <div className="bg-green-500/20 border border-green-500/30 rounded-xl p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                      <Check className="h-5 w-5 text-green-400" />
                    </div>
                    <div>
                      <p className="font-semibold text-green-400">Comment posted!</p>
                      <p className="text-sm text-gray-400">{quickCommentText}</p>
                    </div>
                  </div>
                ) : (
                  /* Comment Input */
                  <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-4">
                    <div className="flex items-start gap-3">
                      {/* User Avatar */}
                      <div className="flex-shrink-0">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center font-bold text-white text-sm">
                          {customer?.full_name?.charAt(0)?.toUpperCase() || customer?.email?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                      </div>
                      
                      {/* Input Area */}
                      <div className="flex-1">
                        <input
                          type="text"
                          placeholder="Add a comment..."
                          value={quickCommentText}
                          onChange={(e) => setQuickCommentText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && quickCommentText.trim()) {
                              submitQuickComment();
                            }
                          }}
                          className="w-full bg-transparent border-none text-white placeholder:text-gray-400 focus:outline-none text-sm"
                          autoFocus
                        />
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setShowQuickComment(false);
                            setQuickCommentText('');
                          }}
                          className="text-gray-400 hover:text-white transition-colors p-1"
                        >
                          <X className="h-5 w-5" />
                        </button>
                        <button
                          onClick={submitQuickComment}
                          disabled={!quickCommentText.trim() || quickCommentSubmitting}
                          className="bg-purple-600 hover:bg-purple-500 disabled:opacity-50 disabled:hover:bg-purple-600 rounded-full p-2 transition-colors"
                        >
                          {quickCommentSubmitting ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Send className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                    
                    {/* Timestamp indicator */}
                    <div className="flex items-center justify-between mt-2 ml-12">
                      <p className="text-xs text-gray-500">Press Enter to submit</p>
                      <span className="text-xs text-purple-400 flex items-center gap-1">
                        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                        Commenting at {formatTime(videoPlayerRef.current?.currentTime || videoCurrentTime || 0)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Next Up - Lower Third Recommendation Overlay */}
            {showNextUpOverlay && nextVideoRecommendation && !showQuickComment && (
              <div 
                className="absolute bottom-20 right-4 left-4 sm:left-auto sm:w-80 z-30 animate-in slide-in-from-right duration-500"
                data-testid="next-up-overlay"
              >
                <div className="bg-black/90 backdrop-blur-xl border border-purple-500/30 rounded-xl overflow-hidden shadow-2xl">
                  {/* Header with dismiss button */}
                  <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse" />
                      <span className="text-xs font-medium text-purple-300 uppercase tracking-wide">
                        Up Next
                      </span>
                      {nextVideoRecommendation.recommendation_source === 'creator' && nextVideoRecommendation.creator_name && (
                        <span className="text-xs text-gray-400">
                          from {nextVideoRecommendation.creator_name}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        setShowNextUpOverlay(false);
                        setNextUpDismissed(true);
                      }}
                      className="text-gray-500 hover:text-white transition-colors p-1 rounded-full hover:bg-white/10"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  
                  {/* Video Preview */}
                  <Link
                    to={`/vod/video/${nextVideoRecommendation.slug || nextVideoRecommendation.id}`}
                    className="flex gap-3 p-3 hover:bg-white/5 transition-colors group"
                    onClick={() => {
                      // Pause current video when navigating
                      if (videoPlayerRef.current) {
                        videoPlayerRef.current.pause();
                        setVideoPlaying(false);
                      }
                    }}
                  >
                    {/* Thumbnail */}
                    <div className="relative flex-shrink-0 w-28 aspect-video rounded-lg overflow-hidden bg-gray-800">
                      {nextVideoRecommendation.thumbnail_url ? (
                        <img 
                          src={nextVideoRecommendation.thumbnail_url} 
                          alt={nextVideoRecommendation.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Play className="h-6 w-6 text-gray-600" />
                        </div>
                      )}
                      {/* Play icon overlay on hover */}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center">
                          <Play className="h-4 w-4 text-black ml-0.5" fill="black" />
                        </div>
                      </div>
                      {/* Duration badge */}
                      {nextVideoRecommendation.duration && (
                        <span className="absolute bottom-1 right-1 bg-black/80 px-1.5 py-0.5 rounded text-xs text-white">
                          {formatDuration(nextVideoRecommendation.duration)}
                        </span>
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-white text-sm line-clamp-2 group-hover:text-purple-300 transition-colors">
                        {nextVideoRecommendation.title}
                      </h4>
                      {nextVideoRecommendation.creator_name && (
                        <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {nextVideoRecommendation.creator_name}
                        </p>
                      )}
                      <p className="text-xs text-purple-400 mt-2 font-medium">
                        Watch Now →
                      </p>
                    </div>
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Continue Watching / Resume Prompt Modal (for logged-in users) */}
      {showResumePrompt && savedProgress && isLoggedIn && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-2xl p-6 max-w-sm mx-4 border border-purple-500/30 shadow-2xl animate-in zoom-in-95 duration-200">
            {/* Close button */}
            <button
              onClick={() => handleResumeChoice(false)}
              className="absolute top-3 right-3 text-gray-500 hover:text-white transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
            
            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <Play className="h-7 w-7 text-white ml-1" />
              </div>
            </div>
            
            {/* Content */}
            <div className="text-center mb-5">
              <h3 className="text-xl font-bold text-white mb-2">
                Continue Watching?
              </h3>
              <p className="text-gray-400 text-sm">
                You left off at <span className="text-purple-400 font-semibold">{formatTime(savedProgress.progress)}</span>
              </p>
              <p className="text-gray-500 text-xs mt-1">
                {Math.round(savedProgress.completion_rate)}% complete
              </p>
            </div>
            
            {/* Progress Bar Visual */}
            <div className="mb-5">
              <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all"
                  style={{ width: `${savedProgress.completion_rate}%` }}
                />
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="space-y-2">
              <Button
                onClick={() => handleResumeChoice(true)}
                className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-semibold"
              >
                <Play className="h-4 w-4 mr-2" />
                Continue from {formatTime(savedProgress.progress)}
              </Button>
              <button
                onClick={() => handleResumeChoice(false)}
                className="w-full py-3 text-gray-400 hover:text-white text-sm transition-colors"
              >
                Start from Beginning
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Full-Screen Comment Modal */}
      {/* Mobile Comment Modal - Floating overlay that keeps video visible */}
      {showMobileCommentModal && isMobile && (
        <div className="fixed inset-0 z-[200] flex flex-col">
          {/* Semi-transparent backdrop - tap to close */}
          <div 
            className="absolute inset-0 bg-black/60"
            onClick={() => closeMobileCommentModal()}
          />
          
          {/* Video stays visible at top (shrunk view) */}
          <div className="relative z-10 bg-black pt-2">
            <div className="px-4 pb-2">
              <div className="aspect-video w-full max-h-[30vh] rounded-xl overflow-hidden bg-black relative">
                {/* Show the actual playing video */}
                {playingInline && videoPlayerRef.current && (
                  <video
                    ref={videoPlayerRef}
                    className="w-full h-full object-contain"
                    src={video?.r2_url_4k || video?.r2_url_1080p || video?.video_url || ''}
                    playsInline
                    controls={false}
                    controlsList="nodownload noplaybackrate"
                    onContextMenu={(e) => e.preventDefault()}
                  />
                )}
                {!playingInline && video?.thumbnail_url && (
                  <img 
                    src={video.thumbnail_url} 
                    alt={video.title}
                    className="w-full h-full object-cover"
                  />
                )}
                {/* Live timestamp indicator */}
                <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/80 rounded text-xs text-white font-medium flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
                  {formatTime(videoCurrentTime)}
                </div>
                {/* Video title */}
                <div className="absolute top-2 left-2 right-2">
                  <p className="text-white text-sm font-medium truncate bg-black/60 px-2 py-1 rounded">{video?.title}</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Comment Input Panel - Bottom Sheet */}
          <div className="relative z-10 mt-auto bg-gradient-to-t from-gray-900 via-gray-900 to-gray-900/95 rounded-t-3xl border-t border-white/10 animate-in slide-in-from-bottom duration-300">
            {/* Handle bar */}
            <div className="flex justify-center py-2">
              <div className="w-10 h-1 bg-white/20 rounded-full"></div>
            </div>
            
            {/* Header */}
            <div className="flex items-center justify-between px-4 pb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center font-bold text-white text-sm">
                  {customer?.username?.charAt(0)?.toUpperCase() || customer?.full_name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <span className="text-white font-medium text-sm">@{customer?.username || customer?.full_name}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 bg-purple-500/20 border border-purple-500/30 rounded-full text-xs text-purple-300 font-medium">
                  @ {formatTime(mobileCommentTimestamp)}
                </span>
                <button
                  onClick={() => closeMobileCommentModal()}
                  className="p-1.5 hover:bg-white/10 rounded-full transition-colors"
                >
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              </div>
            </div>
            
            {/* Success State */}
            {quickCommentSuccess ? (
              <div className="p-6 text-center">
                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-3">
                  <Check className="h-8 w-8 text-green-400" />
                </div>
                <h4 className="text-lg font-semibold text-green-400">Comment Posted!</h4>
              </div>
            ) : (
              /* Comment Input */
              <div className="px-4 pb-4">
                <div className="relative">
                  <Textarea
                    value={quickCommentText}
                    onChange={(e) => setQuickCommentText(e.target.value)}
                    placeholder="Share your thoughts on this moment..."
                    className="w-full min-h-[80px] max-h-[120px] bg-white/5 border-white/10 text-white placeholder:text-gray-500 text-base resize-none focus:border-purple-500/50 focus:ring-purple-500/20 rounded-xl p-3 pr-12"
                    autoFocus
                  />
                </div>
                
                {/* Actions Row */}
                <div className="flex items-center justify-between mt-3">
                  {/* Emoji Button */}
                  <div className="relative">
                    <button
                      onClick={() => setShowMobileEmojiPicker(!showMobileEmojiPicker)}
                      className={`p-2 rounded-lg transition-colors ${
                        showMobileEmojiPicker ? 'bg-purple-500/20 text-purple-400' : 'text-gray-400 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      <Smile className="h-5 w-5" />
                    </button>
                    
                    {/* Emoji Picker */}
                    {showMobileEmojiPicker && (
                      <div className="absolute bottom-full left-0 mb-2 bg-gray-800 border border-white/20 rounded-xl p-2 shadow-xl">
                        <div className="grid grid-cols-8 gap-1">
                          {['😀', '😂', '🥰', '😍', '🤩', '👏', '🔥', '💯', '❤️', '💜', '👀', '😎', '🤔', '😮', '🙌', '✨'].map((emoji) => (
                            <button
                              key={emoji}
                              onClick={() => {
                                setQuickCommentText(prev => prev + emoji);
                                setShowMobileEmojiPicker(false);
                              }}
                              className="w-8 h-8 flex items-center justify-center hover:bg-white/10 rounded text-lg"
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Post Button */}
                  <Button
                    onClick={submitQuickComment}
                    disabled={!quickCommentText.trim() || quickCommentSubmitting}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 rounded-full px-6"
                  >
                    {quickCommentSubmitting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Post
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Signup Prompt Modal (for non-logged in users watching free videos) */}
      {showSignupPrompt && !isLoggedIn && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-2xl p-8 max-w-md mx-4 border border-purple-500/30 shadow-2xl">
            {/* Close button */}
            <button
              onClick={() => {
                setShowSignupPrompt(false);
                // Resume video playback at the same position
                if (videoPlayerRef.current) {
                  videoPlayerRef.current.play().catch(() => {});
                  setVideoPlaying(true);
                }
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
            
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <Bell className="h-8 w-8 text-white" />
              </div>
            </div>
            
            {/* Content - Dynamic from admin settings */}
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-white mb-3">
                {popupSettings.title || 'Enjoying the content?'}
              </h3>
              <p className="text-gray-400">
                {popupSettings.message ? (
                  <>
                    {popupSettings.message.includes('FREE') ? (
                      <>
                        {popupSettings.message.split('FREE')[0]}
                        <span className="text-green-400 font-semibold">FREE</span>
                        {popupSettings.message.split('FREE')[1]}
                      </>
                    ) : popupSettings.message}
                  </>
                ) : (
                  <>Create a <span className="text-green-400 font-semibold">FREE account</span> to stay updated with:</>
                )}
              </p>
            </div>
            
            {/* Benefits - Dynamic from admin settings */}
            <div className="space-y-3 mb-8">
              {(popupSettings.benefits || [
                'New episode releases',
                'Exclusive series & documentaries',
                'Live concerts & shows',
                'Save your watch progress'
              ]).map((benefit, index) => (
                <div key={index} className="flex items-center gap-3 text-gray-300">
                  <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="h-3 w-3 text-purple-400" />
                  </div>
                  <span>{benefit}</span>
                </div>
              ))}
            </div>
            
            {/* CTA Buttons - Dynamic text from admin settings */}
            <div className="space-y-3">
              <Button
                onClick={() => {
                  // Save current video position before redirecting to signup
                  const currentTime = videoPlayerRef.current?.currentTime || 0;
                  const videoSlug = video?.slug || video?.id || videoId;
                  
                  // Store in sessionStorage for after signup redirect
                  sessionStorage.setItem('vodResumeVideo', JSON.stringify({
                    videoSlug,
                    timestamp: currentTime,
                    shouldAutoPlay: true
                  }));
                  
                  // Navigate to signup with autoplay parameter
                  navigate(getSignupUrl(`/vod/video/${videoSlug}?autoplay=true&t=${Math.floor(currentTime)}`));
                }}
                className="w-full py-5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-semibold"
              >
                {popupSettings.cta_button_text || 'Create Free Account'}
              </Button>
              <button
                onClick={() => {
                  setShowSignupPrompt(false);
                  // Resume video playback at the same position (video is already loaded, just paused)
                  if (videoPlayerRef.current) {
                    videoPlayerRef.current.play().catch(() => {});
                    setVideoPlaying(true);
                  }
                }}
                className="w-full text-gray-400 hover:text-white text-sm transition-colors py-2"
              >
                {popupSettings.dismiss_button_text || 'Maybe later'}
              </button>
            </div>
            
            {/* Note */}
            <p className="text-center text-gray-500 text-xs mt-4">
              No credit card required. Free forever.
            </p>
          </div>
        </div>
      )}

      {/* Hero Section - Modern Cinematic Layout - Hide in landscape fullscreen */}
      <div className={`relative`}>
        <style>{`
          /* In PORTRAIT mode - hide the tap overlay, show controls normally */
          @media screen and (orientation: portrait) {
            .vod-tap-overlay {
              display: none !important;
            }
          }
          
          @media screen and (orientation: landscape) and (max-width: 926px) {
            .vod-landscape-hide-content {
              display: none !important;
            }
            
            /* Tap overlay visible in landscape to catch taps */
            .vod-tap-overlay {
              display: block !important;
            }
            
            /* IMPORTANT: Hide video controls by default in landscape on mobile */
            .vod-video-controls-overlay {
              opacity: 0 !important;
              pointer-events: none !important;
              transition: opacity 0.3s ease !important;
            }
            
            /* Show controls only when this class is added */
            .vod-video-controls-overlay.controls-visible {
              opacity: 1 !important;
              pointer-events: auto !important;
            }
            
            /* Also hide center play button by default in landscape */
            .vod-center-play-button {
              opacity: 0 !important;
              pointer-events: none !important;
              transition: opacity 0.3s ease !important;
            }
            
            .vod-center-play-button.controls-visible {
              opacity: 1 !important;
              pointer-events: auto !important;
            }
            
            .vod-landscape-controls-hidden {
              opacity: 0 !important;
              pointer-events: none !important;
              transition: opacity 0.3s ease !important;
            }
            .vod-landscape-controls-visible {
              opacity: 1 !important;
              pointer-events: auto !important;
              transition: opacity 0.3s ease !important;
            }
            .vod-landscape-fullscreen {
              position: fixed !important;
              top: 0 !important;
              left: 0 !important;
              right: 0 !important;
              bottom: 0 !important;
              width: 100vw !important;
              height: 100vh !important;
              z-index: 9999 !important;
              background: black !important;
              border-radius: 0 !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
              padding: 0 !important;
              margin: 0 !important;
            }
            .vod-landscape-fullscreen > div,
            .vod-landscape-fullscreen .aspect-video {
              width: 100% !important;
              height: 100% !important;
              max-width: 100vw !important;
              max-height: 100vh !important;
              aspect-ratio: unset !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
              padding: 0 !important;
              margin: 0 !important;
              border-radius: 0 !important;
              position: relative !important;
            }
            /* VODPlayer container in landscape fullscreen */
            .vod-landscape-fullscreen .vod-player {
              width: 100% !important;
              height: 100% !important;
              position: absolute !important;
              top: 0 !important;
              left: 0 !important;
              right: 0 !important;
              bottom: 0 !important;
            }
            .vod-landscape-fullscreen video,
            .vod-landscape-fullscreen iframe {
              width: 100% !important;
              height: 100% !important;
              max-width: 100vw !important;
              max-height: 100vh !important;
              object-fit: contain !important;
              position: absolute !important;
              top: 0 !important;
              left: 0 !important;
            }
            /* Ad container fullscreen in landscape - transparent background */
            .vod-landscape-fullscreen .ad-container-landscape {
              position: fixed !important;
              top: 0 !important;
              left: 0 !important;
              right: 0 !important;
              bottom: 0 !important;
              width: 100vw !important;
              height: 100vh !important;
              z-index: 9998 !important;
              background: transparent !important;
            }
            .vod-landscape-fullscreen .ad-container-landscape iframe,
            .vod-landscape-fullscreen .ad-container-landscape video {
              width: 100% !important;
              height: 100% !important;
              max-width: 100vw !important;
              max-height: 100vh !important;
              object-fit: contain !important;
              background: black !important;
            }
            /* Ad controls overlay - ensure they stay visible above ad container */
            .vod-landscape-fullscreen .ad-controls-overlay {
              position: fixed !important;
              top: 0 !important;
              left: 0 !important;
              right: 0 !important;
              z-index: 10001 !important;
              padding: 12px !important;
              display: flex !important;
              align-items: center !important;
              justify-content: space-between !important;
              pointer-events: auto !important;
            }
            /* Skip button positioning in landscape */
            .vod-landscape-fullscreen .ad-skip-button-container {
              position: fixed !important;
              bottom: 60px !important;
              right: 12px !important;
              z-index: 10001 !important;
              pointer-events: auto !important;
            }
            /* Ad pause button center overlay */
            .vod-landscape-fullscreen .ad-pause-overlay {
              position: fixed !important;
              top: 0 !important;
              left: 0 !important;
              right: 0 !important;
              bottom: 0 !important;
              z-index: 10000 !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
            }
          }
          @media screen and (orientation: landscape) and (max-height: 600px) {
            .vod-landscape-hide-content {
              display: none !important;
            }
            
            /* IMPORTANT: Hide video controls by default in landscape on mobile */
            .vod-video-controls-overlay {
              opacity: 0 !important;
              pointer-events: none !important;
              transition: opacity 0.3s ease !important;
            }
            
            /* Show controls only when this class is added */
            .vod-video-controls-overlay.controls-visible {
              opacity: 1 !important;
              pointer-events: auto !important;
            }
            
            /* Also hide center play button by default in landscape */
            .vod-center-play-button {
              opacity: 0 !important;
              pointer-events: none !important;
              transition: opacity 0.3s ease !important;
            }
            
            .vod-center-play-button.controls-visible {
              opacity: 1 !important;
              pointer-events: auto !important;
            }
            
            .vod-landscape-controls-hidden {
              opacity: 0 !important;
              pointer-events: none !important;
              transition: opacity 0.3s ease !important;
            }
            .vod-landscape-controls-visible {
              opacity: 1 !important;
              pointer-events: auto !important;
              transition: opacity 0.3s ease !important;
            }
            .vod-landscape-fullscreen {
              position: fixed !important;
              top: 0 !important;
              left: 0 !important;
              right: 0 !important;
              bottom: 0 !important;
              width: 100vw !important;
              height: 100vh !important;
              z-index: 9999 !important;
              background: black !important;
              border-radius: 0 !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
              padding: 0 !important;
              margin: 0 !important;
            }
            .vod-landscape-fullscreen > div,
            .vod-landscape-fullscreen .aspect-video {
              width: 100% !important;
              height: 100% !important;
              max-width: 100vw !important;
              max-height: 100vh !important;
              aspect-ratio: unset !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
              padding: 0 !important;
              margin: 0 !important;
              border-radius: 0 !important;
              position: relative !important;
            }
            /* VODPlayer container in landscape fullscreen */
            .vod-landscape-fullscreen .vod-player {
              width: 100% !important;
              height: 100% !important;
              position: absolute !important;
              top: 0 !important;
              left: 0 !important;
              right: 0 !important;
              bottom: 0 !important;
            }
            .vod-landscape-fullscreen video,
            .vod-landscape-fullscreen iframe {
              width: 100% !important;
              height: 100% !important;
              max-width: 100vw !important;
              max-height: 100vh !important;
              object-fit: contain !important;
              position: absolute !important;
              top: 0 !important;
              left: 0 !important;
            }
            /* Ad container fullscreen in landscape - transparent background */
            .vod-landscape-fullscreen .ad-container-landscape {
              position: fixed !important;
              top: 0 !important;
              left: 0 !important;
              right: 0 !important;
              bottom: 0 !important;
              width: 100vw !important;
              height: 100vh !important;
              z-index: 9998 !important;
              background: transparent !important;
            }
            .vod-landscape-fullscreen .ad-container-landscape iframe,
            .vod-landscape-fullscreen .ad-container-landscape video {
              width: 100% !important;
              height: 100% !important;
              max-width: 100vw !important;
              max-height: 100vh !important;
              object-fit: contain !important;
              background: black !important;
            }
            /* Ad controls overlay - ensure they stay visible above ad container */
            .vod-landscape-fullscreen .ad-controls-overlay {
              position: fixed !important;
              top: 0 !important;
              left: 0 !important;
              right: 0 !important;
              z-index: 10001 !important;
              padding: 12px !important;
              display: flex !important;
              align-items: center !important;
              justify-content: space-between !important;
              pointer-events: auto !important;
            }
            /* Skip button positioning in landscape */
            .vod-landscape-fullscreen .ad-skip-button-container {
              position: fixed !important;
              bottom: 60px !important;
              right: 12px !important;
              z-index: 10001 !important;
              pointer-events: auto !important;
            }
            /* Ad pause button center overlay */
            .vod-landscape-fullscreen .ad-pause-overlay {
              position: fixed !important;
              top: 0 !important;
              left: 0 !important;
              right: 0 !important;
              bottom: 0 !important;
              z-index: 10000 !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
            }
          }
        `}</style>
        
        {/* Minimal Google IMA SDK styling - let IMA handle its own elements */}
        <style>{`
          /* Ad container - just needs to be positioned and sized */
          .ad-container-landscape {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            overflow: visible;
          }
          
          /* In fullscreen mode, make container fill viewport */
          .vod-landscape-fullscreen .ad-container-landscape {
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            z-index: 9998;
          }
        `}</style>
        
        {/* Background gradient overlay */}
        <div className={`absolute inset-0 bg-gradient-to-b from-black via-[#0a0a0a] to-[#141414] pointer-events-none ${playingInline ? 'vod-landscape-hide-content' : ''}`} />
        
        <div className="relative w-full px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
          {/* Main Content Grid - Fixed layout to prevent overlap */}
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-6 lg:justify-end">
            
            {/* Video Thumbnail / Inline Player - Right side on desktop */}
            <div className={`${playingInline ? 'vod-landscape-fullscreen' : ''} ${(isFullscreen || (isLandscape && playingInline && isMobile)) ? 'fixed inset-0 z-[9999] bg-black' : 'w-full lg:w-[70%] lg:order-2 flex-shrink-0'}`}>
              <div 
                ref={videoContainerRef}
                className={`relative overflow-hidden shadow-2xl shadow-black/50 ${(isFullscreen || (isLandscape && playingInline && isMobile)) ? 'w-full h-full' : 'aspect-video rounded-2xl'}`}
                onContextMenu={(e) => e.preventDefault()}
              >
                {playingInline ? (
                  /* INLINE VIDEO PLAYER */
                  <>
                    {/* Thumbnail as background - only show when video hasn't started yet */}
                    {video.thumbnail_url && !videoPlaying && videoCurrentTime === 0 && (
                      <div 
                        className="absolute inset-0 z-0"
                        style={{
                          backgroundImage: `url(${video.thumbnail_url})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                        }}
                      />
                    )}
                    
                    {/* Check if valid video source exists */}
                    {(video.r2_url_4k || video.r2_url_1080p || (video.video_url && video.video_url.startsWith('http')) || video.cloudflare_video_id) ? (
                      /* Use Cloudflare Stream native player for CF videos, VODPlayer for others */
                      video.cloudflare_video_id ? (
                        /* Cloudflare Stream player with Google IMA ads */
                        <CloudflareStreamPlayer
                          ref={videoPlayerRef}
                          videoId={video.cloudflare_video_id}
                          videoSlug={video.slug || video.id}
                          poster={video.thumbnail_url || ''}
                          autoPlay={!savedProgress}
                          enableAds={shouldShowAds}
                          className="w-full h-full"
                          style={{ height: '100%', zIndex: 1 }}
                          onTimeUpdate={(currentTime, duration) => {
                            setVideoCurrentTime(currentTime);
                            setVideoDuration(duration);
                            const progress = (currentTime / duration) * 100;
                            setVideoProgress(progress);
                            updateWatchTracking(currentTime, duration);
                          }}
                          onPlay={() => {
                            setVideoPlaying(true);
                            sendHeartbeat();
                          }}
                          onPause={() => {
                            setVideoPlaying(false);
                            sendHeartbeat();
                          }}
                          onEnded={() => {
                            setVideoPlaying(false);
                            sendHeartbeat();
                          }}
                          onAdStart={() => {
                            setInlineAdPlaying(true);
                          }}
                          onAdEnd={() => {
                            setInlineAdPlaying(false);
                          }}
                        />
                      ) : (
                        /* VOD Player for R2/direct URLs */
                        <VODPlayer
                          ref={videoPlayerRef}
                          src={video.video_url && video.video_url.startsWith('http') ? video.video_url : ''}
                          src4k={video.r2_url_4k || null}
                          srcHd={video.r2_url_1080p || null}
                          videoSlug={video.slug || video.id}
                          videoId={video.id}
                          poster={video.thumbnail_url || ''}
                          enableAds={shouldShowAds}
                          autoPlay={!savedProgress}
                          skipPreRollProp={skipPreRollOnResume}
                          isLandscapeFullscreen={isFullscreen || (isLandscape && playingInline && isMobile)}
                          className="w-full h-full"
                          style={{ height: '100%', zIndex: 1 }}
                          onTimeUpdate={(currentTime, duration) => {
                            setVideoCurrentTime(currentTime);
                            setVideoDuration(duration);
                            const progress = (currentTime / duration) * 100;
                            setVideoProgress(progress);
                            updateWatchTracking(currentTime, duration);
                          }}
                          onPlay={() => {
                            setVideoPlaying(true);
                            sendHeartbeat();
                          }}
                          onPause={() => {
                            setVideoPlaying(false);
                            sendHeartbeat();
                          }}
                          onEnded={() => {
                            setVideoPlaying(false);
                            sendHeartbeat();
                          }}
                          onError={(err) => {
                            console.error('[VODVideoDetail] Player error:', err);
                          }}
                          onAdStart={() => {
                            setInlineAdPlaying(true);
                          }}
                          onAdEnd={() => {
                            setInlineAdPlaying(false);
                          }}
                          onQualityChange={(quality) => {
                            console.log('[VODVideoDetail] Quality changed to:', quality);
                          }}
                        />
                      )
                    ) : (
                      /* No valid video source - show message */
                      <div className="w-full h-full bg-black flex flex-col items-center justify-center text-center p-4 sm:p-8 z-50 relative">
                        <Film className="h-12 w-12 sm:h-16 sm:w-16 text-gray-600 mb-3 sm:mb-4" />
                        <h3 className="text-lg sm:text-xl font-semibold text-white mb-2">Video Not Available</h3>
                        <p className="text-gray-400 mb-4 max-w-md text-sm sm:text-base">
                          This video is not available for playback yet. Please check back later or contact support.
                        </p>
                        <Button
                          onClick={(e) => { e.stopPropagation(); stopInlineVideo(); }}
                          variant="outline"
                          className="border-white/30 text-white hover:bg-white/10 z-50"
                        >
                          <X className="h-4 w-4 mr-2" />
                          Close
                        </Button>
                      </div>
                    )}
                    
                    {/* Close button for inline player — auto-hide in landscape fullscreen */}
                    <button 
                      onClick={(e) => { e.stopPropagation(); stopInlineVideo(); }}
                      className={`absolute top-3 right-3 z-50 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition-all duration-300 ${
                        (isLandscape && isMobile && playingInline && !showLandscapeControls) ? 'opacity-0 pointer-events-none' : 'opacity-100'
                      }`}
                      title="Close"
                    >
                      <X className="h-5 w-5" />
                    </button>
                    
                    {/* Next Up - Lower Third Recommendation Overlay */}
                    {showNextUpOverlay && nextVideoRecommendation && !inlineAdPlaying && (
                      <div 
                        className="absolute bottom-20 right-4 left-4 sm:left-auto sm:w-80 z-40 animate-in slide-in-from-right duration-500"
                        data-testid="next-up-overlay"
                      >
                        <div className="bg-black/90 backdrop-blur-xl border border-purple-500/30 rounded-xl overflow-hidden shadow-2xl">
                          {/* Header with dismiss button */}
                          <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse" />
                              <span className="text-xs font-medium text-purple-300 uppercase tracking-wide">
                                Up Next
                              </span>
                              {nextVideoRecommendation.recommendation_source === 'creator' && nextVideoRecommendation.creator_name && (
                                <span className="text-xs text-gray-400">
                                  from {nextVideoRecommendation.creator_name}
                                </span>
                              )}
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowNextUpOverlay(false);
                                setNextUpDismissed(true);
                              }}
                              className="text-gray-500 hover:text-white transition-colors p-1 rounded-full hover:bg-white/10"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                          
                          {/* Video Preview */}
                          <Link
                            to={`/vod/video/${nextVideoRecommendation.slug || nextVideoRecommendation.id}`}
                            className="flex gap-3 p-3 hover:bg-white/5 transition-colors group"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Pause current video when navigating
                              if (videoPlayerRef.current) {
                                videoPlayerRef.current.pause();
                                setVideoPlaying(false);
                              }
                            }}
                          >
                            {/* Thumbnail */}
                            <div className="relative flex-shrink-0 w-28 aspect-video rounded-lg overflow-hidden bg-gray-800">
                              {nextVideoRecommendation.thumbnail_url ? (
                                <img 
                                  src={nextVideoRecommendation.thumbnail_url} 
                                  alt={nextVideoRecommendation.title}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Play className="h-6 w-6 text-gray-600" />
                                </div>
                              )}
                              {/* Play icon overlay on hover */}
                              <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                                <div className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center">
                                  <Play className="h-4 w-4 text-black ml-0.5" fill="black" />
                                </div>
                              </div>
                              {/* Duration badge */}
                              {nextVideoRecommendation.duration && (
                                <span className="absolute bottom-1 right-1 bg-black/80 px-1.5 py-0.5 rounded text-xs text-white">
                                  {formatDuration(nextVideoRecommendation.duration)}
                                </span>
                              )}
                            </div>
                            
                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-white text-sm line-clamp-2 group-hover:text-purple-300 transition-colors">
                                {nextVideoRecommendation.title}
                              </h4>
                              {nextVideoRecommendation.creator_name && (
                                <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                                  <Users className="h-3 w-3" />
                                  {nextVideoRecommendation.creator_name}
                                </p>
                              )}
                              <p className="text-xs text-purple-400 mt-2 font-medium">
                                Watch Now →
                              </p>
                            </div>
                          </Link>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  /* THUMBNAIL VIEW - Click to play */
                  <div 
                    className={`w-full h-full group ${isVideoPremiere(video) ? 'cursor-default' : 'cursor-pointer'}`}
                    onClick={() => {
                      // Block playback if premiere hasn't started yet
                      if (isVideoPremiere(video)) {
                        return; // Don't allow play
                      }
                      handleWatch();
                    }}
                  >
                    {/* Thumbnail Image */}
                    <img 
                      src={video.thumbnail_url || '/placeholder.jpg'} 
                      alt={video.title}
                      className={`w-full h-full object-cover transition-transform duration-500 ${isVideoPremiere(video) ? '' : 'group-hover:scale-105'}`}
                    />
                    
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    
                    {/* PREMIERE COUNTDOWN OVERLAY - Shows on video player when premiere hasn't started */}
                    {isVideoPremiere(video) ? (
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 backdrop-blur-sm z-20">
                        {/* Premiering Soon Badge */}
                        <div className="mb-4 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full text-white text-sm font-semibold flex items-center gap-2">
                          <Sparkles className="h-4 w-4" />
                          Premiering Soon
                        </div>
                        
                        {/* Countdown Timer */}
                        <div className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-mono mb-3">
                          {(() => {
                            // Use premiereCountdown state, or calculate from premiere_date as fallback
                            const countdown = premiereCountdown || getPremiereCountdownSeconds(video);
                            
                            const days = Math.floor(countdown / 86400);
                            const hrs = Math.floor((countdown % 86400) / 3600);
                            const mins = Math.floor((countdown % 3600) / 60);
                            const secs = countdown % 60;
                            
                            if (days > 0) return `${days}d ${hrs}h ${mins}m`;
                            if (hrs > 0) return `${hrs}h ${mins}m ${secs}s`;
                            if (mins > 0) return `${mins}m ${secs}s`;
                            if (countdown > 0) return `${secs}s`;
                            return 'Starting soon...';
                          })()}
                        </div>
                        
                        {/* Premiere Date */}
                        <p className="text-sm sm:text-base text-gray-300 mb-5">
                          Premieres {video.premiere_date && new Date(video.premiere_date).toLocaleString(undefined, {
                            weekday: 'long', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                          })}
                        </p>
                        
                        {/* Remind Me Button */}
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleReminder();
                          }}
                          disabled={reminderLoading}
                          className={`px-6 py-3 ${hasReminder 
                            ? 'bg-green-600 hover:bg-green-700 text-white' 
                            : 'bg-purple-600 hover:bg-purple-500 text-white'
                          } rounded-full font-semibold flex items-center gap-2`}
                          data-testid="premiere-remind-btn"
                        >
                          {reminderLoading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : hasReminder ? (
                            <>
                              <CheckCircle className="h-4 w-4" />
                              Reminder Set
                            </>
                          ) : (
                            <>
                              <Bell className="h-4 w-4" />
                              Remind Me
                            </>
                          )}
                        </Button>
                        
                        {/* People waiting count */}
                        {reminderCount > 0 && (
                          <p className="text-xs text-gray-400 mt-3">
                            {reminderCount} {reminderCount === 1 ? 'person' : 'people'} waiting
                          </p>
                        )}
                      </div>
                    ) : (
                      /* Play Button or Paywall Lock - Center */
                      <div className="absolute inset-0 flex items-center justify-center">
                        {(video.price || 0) > 0 && !video.has_purchased && video.access_type !== 'purchase' ? (
                          /* Locked - Paid video not purchased */
                          <div className="flex flex-col items-center gap-2 cursor-pointer" onClick={() => { if (isLoggedIn) { handlePurchase(); } else { setShowPaywall(true); } }} data-testid="paywall-lock-overlay">
                            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/20 transition-all duration-300 group-hover:scale-110 group-hover:bg-purple-600/40">
                              <Lock className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
                            </div>
                            <div className="bg-black/70 backdrop-blur-sm px-3 py-1.5 rounded-full text-white text-sm font-semibold">
                              {"\u00A3"}{(video.sale_price || video.price || 0).toFixed(2)}
                            </div>
                          </div>
                        ) : (
                          /* Unlocked - Free or purchased */
                          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:bg-purple-600/80 border border-white/30">
                            <Play className="h-7 w-7 sm:h-8 sm:w-8 text-white fill-white ml-1" />
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Duration Badge */}
                    {video.duration > 0 && !isVideoPremiere(video) && (
                      <div className="absolute bottom-3 right-3 px-2.5 py-1 bg-black/80 backdrop-blur-sm rounded-md text-white text-sm font-medium">
                        {formatDuration(video.duration)}
                      </div>
                    )}
                    
                    {/* 4K Badge */}
                    {video.has_4k && (
                      <div className="absolute top-3 right-3 px-2 py-1 bg-gradient-to-r from-amber-500 to-orange-500 rounded text-black text-xs font-bold">
                        4K
                      </div>
                    )}
                    
                    {/* Premium Badge */}
                    {video.tier === 'premium' && (
                      <div className="absolute top-3 left-3 px-2.5 py-1 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full text-white text-xs font-semibold flex items-center gap-1">
                        <Crown className="h-3 w-3" /> Premium
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {/* Video Info - Left side on desktop - Hide in landscape mobile */}
            <div className={`w-full lg:w-[30%] lg:order-1 flex flex-col lg:pr-4 ${playingInline ? 'vod-landscape-hide-content' : ''}`}>
              {/* Category Tag */}
              {video.category && (
                <div className="mb-3">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-300 border border-purple-500/30 uppercase tracking-wide">
                    {video.category.name}
                  </span>
                </div>
              )}
              
              {/* Title - Clean Typography */}
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight mb-4">
                {video.title}
              </h1>
              
              {/* Price / Purchase Info */}
              {(video.price || 0) > 0 && (
                <div className="mb-4">
                  {video.has_purchased || video.access_type === 'purchase' ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-green-500/20 text-green-400 border border-green-500/30" data-testid="purchased-badge">
                      <CheckCircle className="h-4 w-4" /> Purchased
                    </span>
                  ) : (
                    <button
                      onClick={() => setShowPaywall(true)}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white transition-all"
                      data-testid="purchase-price-btn"
                    >
                      <CreditCard className="h-4 w-4" />
                      Purchase for {"\u00A3"}{(video.sale_price || video.price || 0).toFixed(2)}
                    </button>
                  )}
                </div>
              )}
              {(video.price || 0) <= 0 && (
                <div className="mb-4">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-emerald-500/20 text-emerald-400 border border-emerald-500/30" data-testid="free-badge">
                    Free to Watch
                  </span>
                </div>
              )}
              
              {/* Stats Row */}
              <div className="flex items-center flex-wrap gap-4 text-sm text-gray-400 mb-5">
                <span className="flex items-center gap-1.5">
                  <Eye className="h-4 w-4 text-purple-400" />
                  {formatViews(video.view_count || 0)} views
                </span>
                {video.duration > 0 && (
                  <span className="flex items-center gap-1.5">
                    <Clock className="h-4 w-4" />
                    {formatDuration(video.duration)}
                  </span>
                )}
                {hasPremiumSubscription && (
                  <span className="flex items-center gap-1.5 text-green-400">
                    <Sparkles className="h-4 w-4" />
                    Ad-Free
                  </span>
                )}
              </div>
              
              {/* Creator Profile Section - MOVED UP */}
              {creatorProfile && (
                <div className="mb-4 p-4 bg-gray-800/50 rounded-xl border border-gray-700/50">
                  <div className="flex items-center justify-between gap-4">
                    <Link 
                      to={`/c/${creatorProfile.slug}`}
                      className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80 transition"
                    >
                      <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden bg-gray-700 flex-shrink-0 border-2 border-purple-500/30">
                        {creatorProfile.profile_image_url ? (
                          <img 
                            src={creatorProfile.profile_image_url} 
                            alt={creatorProfile.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-xl font-bold text-purple-400">
                            {creatorProfile.name?.charAt(0)}
                          </div>
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold text-white truncate">{creatorProfile.name}</span>
                          {creatorProfile.is_verified && (
                            <BadgeCheck className="w-5 h-5 text-purple-400 flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-sm text-gray-400 flex items-center gap-1">
                          <Users className="w-3.5 h-3.5" />
                          {(creatorProfile.subscriber_count || 0).toLocaleString()} subscribers
                        </p>
                      </div>
                    </Link>
                    
                    {isSubscribedToCreator ? (
                      <button
                        onClick={handleUnsubscribeFromCreator}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-full transition text-sm font-medium flex-shrink-0"
                      >
                        <BellOff className="w-4 h-4" />
                        <span className="hidden sm:inline">Subscribed</span>
                      </button>
                    ) : (
                      <button
                        onClick={handleSubscribeToCreator}
                        disabled={subscribingToCreator}
                        className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-full transition text-sm font-medium flex-shrink-0"
                      >
                        {subscribingToCreator ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Bell className="w-4 h-4" />
                        )}
                        <span className="hidden sm:inline">Subscribe</span>
                      </button>
                    )}
                  </div>
                </div>
              )}
              
              {/* Like/Dislike - MOVED DOWN */}
              <div className="flex items-center gap-2 mb-4">
                <button
                  onClick={() => handleReaction('like')}
                  disabled={reactionLoading}
                  data-testid="like-button"
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    userReaction === 'like'
                      ? 'bg-green-500/20 text-green-400 border border-green-500/40'
                      : 'bg-white/5 text-gray-400 hover:bg-white/10 border border-white/10'
                  }`}
                >
                  <ThumbsUp className={`h-4 w-4 ${userReaction === 'like' ? 'fill-current' : ''}`} />
                  {likeCount > 0 ? likeCount : 'Like'}
                </button>
                
                <button
                  onClick={() => handleReaction('dislike')}
                  disabled={reactionLoading}
                  data-testid="dislike-button"
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    userReaction === 'dislike'
                      ? 'bg-red-500/20 text-red-400 border border-red-500/40'
                      : 'bg-white/5 text-gray-400 hover:bg-white/10 border border-white/10'
                  }`}
                >
                  <ThumbsDown className={`h-4 w-4 ${userReaction === 'dislike' ? 'fill-current' : ''}`} />
                  Dislike
                </button>
              </div>

              {/* Action Buttons - ADD COMMENT, Watchlist, Share - MOVED DOWN */}
              <div className="flex flex-wrap gap-2 mb-5">
                {/* ADD COMMENT button */}
                <Button
                  onClick={() => {
                    const currentTime = videoPlayerRef.current?.currentTime || videoCurrentTime || 0;
                    setInlineCommentTimestamp(currentTime);
                    
                    if (isMobile) {
                      setMobileCommentTimestamp(currentTime);
                      setShowMobileCommentModal(true);
                      if (videoPlayerRef.current && !videoPlayerRef.current.paused) {
                        videoPlayerRef.current.pause();
                        setVideoPlaying(false);
                      }
                    } else {
                      if (!playingInline) {
                        setPlayingInline(true);
                      }
                      setShowQuickComment(true);
                    }
                  }}
                  className="py-2.5 px-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-medium rounded-full text-sm"
                  data-testid="add-comment-btn"
                >
                  <MessageCircle className="h-4 w-4 mr-1.5" />
                  ADD COMMENT
                  {playingInline && (
                    <span className="ml-1.5 px-1.5 py-0.5 bg-white/20 rounded text-xs font-mono">
                      {formatTime(videoCurrentTime || 0)}
                    </span>
                  )}
                </Button>
                
                <Button
                  variant="outline"
                  onClick={toggleWatchlist}
                  disabled={watchlistLoading}
                  className={`py-2.5 px-4 rounded-full text-sm ${
                    inWatchlist 
                      ? 'border-purple-500 bg-purple-500/10 text-purple-400' 
                      : 'border-white/20 text-white hover:bg-white/5'
                  }`}
                >
                  {watchlistLoading ? (
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  ) : inWatchlist ? (
                    <BookmarkCheck className="h-4 w-4 mr-1.5" />
                  ) : (
                    <Bookmark className="h-4 w-4 mr-1.5" />
                  )}
                  {inWatchlist ? 'In Watchlist' : 'Add to Watchlist'}
                </Button>
                
                {/* Share Button */}
                <Button
                  variant="outline"
                  onClick={() => setShowShareModal(true)}
                  className="py-2.5 px-4 rounded-full text-sm border-white/20 hover:border-white/40 hover:bg-white/10"
                  data-testid="share-video-btn"
                >
                  <Share2 className="h-4 w-4 mr-1.5" />
                  Share
                </Button>
              </div>
              
              {/* Description - Collapsible */}
              {video.description && (
                <div className="mb-5">
                  <p className={`text-gray-400 text-sm leading-relaxed ${!descriptionExpanded ? 'line-clamp-3' : ''}`}>
                    {video.description}
                  </p>
                  {video.description.length > 150 && (
                    <button 
                      onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                      className="text-purple-400 hover:text-purple-300 text-sm mt-2 font-medium"
                    >
                      {descriptionExpanded ? 'Show less' : 'Show more'}
                    </button>
                  )}
                </div>
              )}
              
              {/* Rating */}
              {(reviewStats.average_rating > 0 || reviewStats.review_count > 0) && (
                <div className="flex items-center gap-2 mt-4">
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`h-4 w-4 ${
                          star <= Math.round(reviewStats.average_rating)
                            ? 'text-amber-400 fill-amber-400'
                            : 'text-gray-600'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-amber-400 font-semibold">{reviewStats.average_rating?.toFixed(1)}</span>
                  <span className="text-gray-500 text-sm">({reviewStats.review_count} reviews)</span>
                </div>
              )}
              
              {/* Inline Comment Input Box - Shows when ADD COMMENT is clicked */}
              {showQuickComment && !isMobile && customer && customer.username && (
                <div className="mt-4 bg-white/5 backdrop-blur-md rounded-xl border border-purple-500/30 p-4">
                  {quickCommentSuccess ? (
                    <div className="flex items-center justify-center gap-2 py-3 text-green-400">
                      <CheckCircle className="h-5 w-5" />
                      <span className="font-medium">Comment posted with timestamp!</span>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-sm font-bold text-white">
                          {customer.username?.charAt(0)?.toUpperCase()}
                        </div>
                        <span className="text-sm text-purple-400 font-medium">@{customer.username}</span>
                        {/* Timestamp badge - only show if video is playing */}
                        {playingInline && (
                          <span className="ml-auto px-3 py-1 bg-purple-500/30 border border-purple-500/40 rounded-full text-sm text-purple-300 flex items-center gap-1.5 font-mono">
                            <Clock className="h-3.5 w-3.5" />
                            {formatTime(inlineCommentTimestamp || videoCurrentTime || 0)}
                          </span>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder={playingInline ? "Add your comment at this timestamp..." : "Add your comment..."}
                          value={quickCommentText}
                          onChange={(e) => setQuickCommentText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey && quickCommentText.trim()) {
                              e.preventDefault();
                              submitQuickComment();
                            }
                          }}
                          className="flex-1 bg-black/50 border border-white/20 rounded-lg px-4 py-3 text-white placeholder:text-gray-500 focus:outline-none focus:border-purple-500/50"
                          autoFocus
                          data-testid="inline-comment-input"
                        />
                        <Button
                          onClick={submitQuickComment}
                          disabled={quickCommentSubmitting || !quickCommentText.trim()}
                          className="px-4 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700"
                          data-testid="submit-inline-comment"
                        >
                          {quickCommentSubmitting ? (
                            <Loader2 className="h-5 w-5 animate-spin" />
                          ) : (
                            <Send className="h-5 w-5" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => { 
                            setShowQuickComment(false); 
                            setQuickCommentText('');
                          }}
                          className="text-gray-400 hover:text-white"
                        >
                          <X className="h-5 w-5" />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Comments Section - Modern Design - Hide in landscape mobile */}
      <section className={`py-12 px-4 border-t border-white/10 ${playingInline ? 'vod-landscape-hide-content' : ''}`}>
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
            <MessageCircle className="h-6 w-6 text-purple-400" />
            Comments
            {reviews.length > 0 && (
              <span className="text-sm font-normal text-gray-500">({reviews.length})</span>
            )}
          </h2>
          
          {/* Comment Prompt - Direct users to ADD COMMENT button */}
          {customer ? (
            customer.username ? (
              // User has username - show prompt to use ADD COMMENT button
              <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-2xl p-5 mb-8 border border-purple-500/20 backdrop-blur-sm">
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center font-bold text-white">
                      {customer.username?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-medium">Want to comment?</p>
                    <p className="text-sm text-gray-400">Use the <span className="text-purple-400 font-medium">ADD COMMENT</span> button above to leave a timestamped comment</p>
                  </div>
                  <Button
                    onClick={() => {
                      // Scroll to top and open comment input
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                      setTimeout(() => {
                        if (!playingInline) {
                          setPlayingInline(true);
                        }
                        setInlineCommentTimestamp(videoPlayerRef.current?.currentTime || videoCurrentTime || 0);
                        setShowQuickComment(true);
                      }, 300);
                    }}
                    className="bg-purple-600 hover:bg-purple-500 rounded-full px-5"
                  >
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Add Comment
                  </Button>
                </div>
              </div>
            ) : (
              // User logged in but no username - show set username prompt
              <div className="bg-gradient-to-br from-white/5 to-white/[0.02] rounded-2xl p-6 mb-8 border border-white/10 backdrop-blur-sm">
                <div className="flex gap-4 items-center">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500/30 to-pink-500/30 flex items-center justify-center">
                      <User className="h-6 w-6 text-purple-400" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-white mb-1">Set your username to comment</h3>
                    <p className="text-sm text-gray-400 mb-3">
                      Create a username to join the conversation. This will be your display name across LTV.
                    </p>
                    <Button
                      onClick={() => setShowUsernameModal(true)}
                      className="bg-purple-600 hover:bg-purple-500 rounded-full px-5"
                      data-testid="set-username-prompt-btn"
                    >
                      <User className="h-4 w-4 mr-2" />
                      Set Username
                    </Button>
                  </div>
                </div>
              </div>
            )
          ) : (
            /* Sign in prompt - Modern Design */
            <div className="bg-gradient-to-br from-white/5 to-white/[0.02] rounded-2xl p-8 mb-8 text-center border border-white/10 backdrop-blur-sm">
              <div className="w-14 h-14 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="h-7 w-7 text-purple-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Join the conversation</h3>
              <p className="text-gray-400 mb-5 text-sm">Sign in to leave a comment and interact with others</p>
              <Button
                onClick={() => navigate(getLoginUrl(`/vod/video/${video?.slug || video?.id || videoId}`))}
                className="bg-purple-600 hover:bg-purple-500 rounded-full px-6"
              >
                Sign In to Comment
              </Button>
            </div>
          )}
          
          {/* Comments List - YouTube-Style Threaded Comments */}
          {reviews.length > 0 ? (
            <div className="space-y-4">
              {reviews.map((review) => (
                <div key={review.id} className="bg-white/[0.03] rounded-xl p-4 border border-white/5">
                  {/* Main Comment */}
                  <div className="flex gap-3 sm:gap-4">
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-bold text-white text-sm ${
                        review.customer_id === customer?.id 
                          ? 'bg-gradient-to-br from-purple-500 to-pink-500' 
                          : 'bg-gradient-to-br from-blue-500 to-purple-500'
                      }`}>
                        {(review.customer_username || review.customer_name)?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                    </div>
                    
                    {/* Comment Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-2 mb-1">
                        <p className="font-medium text-white text-sm sm:text-base">
                          @{review.customer_username || review.customer_name}
                        </p>
                        {review.customer_id === customer?.id && (
                          <span className="px-2 py-0.5 bg-purple-500/20 rounded-full text-xs text-purple-300">You</span>
                        )}
                        {/* Timestamp Badge */}
                        {review.video_timestamp !== null && review.video_timestamp !== undefined && review.video_timestamp > 0 && (
                          <button
                            onClick={() => {
                              if (isMobile) window.scrollTo({ top: 0, behavior: 'smooth' });
                              
                              // Store the timestamp to seek to after ad finishes
                              pendingSeekTimestampRef.current = review.video_timestamp;
                              
                              // If video is already playing inline and no ad is playing, seek immediately
                              if (playingInline && !inlineAdPlaying && !adRequestPending && videoPlayerRef.current) {
                                videoPlayerRef.current.currentTime = review.video_timestamp;
                                videoPlayerRef.current.play().catch(() => {});
                                setVideoPlaying(true);
                                pendingSeekTimestampRef.current = null;
                              } else {
                                // Start inline player - ad will play first, then seek to timestamp
                                setPlayingInline(true);
                                // Don't play video immediately - let onAdEnd handle it
                              }
                            }}
                            className="px-2 py-0.5 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 rounded-full text-xs text-purple-300 font-medium transition-colors flex items-center gap-1"
                          >
                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M8 5v14l11-7z"/>
                            </svg>
                            {formatTime(review.video_timestamp)}
                          </button>
                        )}
                        <span className="text-xs text-gray-500">
                          {new Date(review.created_at).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric',
                            year: new Date(review.created_at).getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
                          })}
                        </span>
                      </div>
                      
                      {/* Comment Text */}
                      {review.review_text && (
                        <p className="text-gray-300 leading-relaxed break-words text-sm sm:text-base mb-2">{review.review_text}</p>
                      )}
                      
                      {/* Action Buttons */}
                      <div className="flex items-center gap-3 mt-2">
                        {/* Reply Button */}
                        {customer && customer.username && (
                          <button
                            onClick={() => setReplyingTo(replyingTo?.id === review.id ? null : { id: review.id, username: review.customer_username || review.customer_name })}
                            className="text-xs text-gray-400 hover:text-purple-400 transition-colors flex items-center gap-1"
                          >
                            <MessageCircle className="w-3.5 h-3.5" />
                            Reply
                          </button>
                        )}
                        
                        {/* Delete Button (own comments only) */}
                        {review.customer_id === customer?.id && (
                          <button
                            onClick={() => deleteComment(review.id)}
                            className="text-xs text-gray-400 hover:text-red-400 transition-colors flex items-center gap-1"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            Delete
                          </button>
                        )}
                        
                        {/* View Replies Button */}
                        {(review.replies?.length > 0 || review.reply_count > 0) && (
                          <button
                            onClick={() => toggleReplies(review.id)}
                            className="text-xs text-purple-400 hover:text-purple-300 transition-colors flex items-center gap-1"
                          >
                            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expandedReplies[review.id] ? 'rotate-180' : ''}`} />
                            {expandedReplies[review.id] ? 'Hide' : 'View'} {review.replies?.length || review.reply_count} {(review.replies?.length || review.reply_count) === 1 ? 'reply' : 'replies'}
                          </button>
                        )}
                      </div>
                      
                      {/* Reply Input Box */}
                      {replyingTo?.id === review.id && (
                        <div className="mt-3 flex gap-2 items-start">
                          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center font-bold text-white text-xs flex-shrink-0">
                            {customer?.username?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                          <div className="flex-1">
                            <input
                              type="text"
                              value={replyText}
                              onChange={(e) => setReplyText(e.target.value)}
                              placeholder={`Reply to @${replyingTo.username}...`}
                              className="w-full bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                              onKeyPress={(e) => e.key === 'Enter' && !submittingReply && submitReply(review.id)}
                              autoFocus
                            />
                            <div className="flex gap-2 mt-2 justify-end">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { setReplyingTo(null); setReplyText(''); }}
                                className="text-gray-400 hover:text-white text-xs h-7 px-3"
                              >
                                Cancel
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => submitReply(review.id)}
                                disabled={!replyText.trim() || submittingReply}
                                className="bg-purple-600 hover:bg-purple-500 text-xs h-7 px-3 rounded-full"
                              >
                                {submittingReply ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Reply'}
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Replies List (Nested) */}
                      {expandedReplies[review.id] && review.replies?.length > 0 && (
                        <div className="mt-4 ml-2 sm:ml-4 pl-3 sm:pl-4 border-l-2 border-white/10 space-y-3">
                          {review.replies.map((reply) => (
                            <div key={reply.id} className="flex gap-2 sm:gap-3">
                              {/* Reply Avatar */}
                              <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-white text-xs flex-shrink-0 ${
                                reply.customer_id === customer?.id 
                                  ? 'bg-gradient-to-br from-purple-500 to-pink-500' 
                                  : 'bg-gradient-to-br from-green-500 to-teal-500'
                              }`}>
                                {(reply.customer_username || reply.customer_name)?.charAt(0)?.toUpperCase() || 'U'}
                              </div>
                              
                              {/* Reply Content */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center flex-wrap gap-2 mb-0.5">
                                  <p className="font-medium text-white text-xs sm:text-sm">
                                    @{reply.customer_username || reply.customer_name}
                                  </p>
                                  {reply.customer_id === customer?.id && (
                                    <span className="px-1.5 py-0.5 bg-purple-500/20 rounded-full text-[10px] text-purple-300">You</span>
                                  )}
                                  <span className="text-[10px] sm:text-xs text-gray-500">
                                    {new Date(reply.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                  </span>
                                </div>
                                <p className="text-gray-300 text-xs sm:text-sm break-words">{reply.review_text}</p>
                                
                                {/* Delete Reply Button */}
                                {reply.customer_id === customer?.id && (
                                  <button
                                    onClick={() => deleteComment(reply.id)}
                                    className="text-[10px] text-gray-500 hover:text-red-400 transition-colors mt-1 flex items-center gap-1"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                    Delete
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="h-8 w-8 text-gray-600" />
              </div>
              <p className="text-gray-500 mb-1">No comments yet</p>
              <p className="text-gray-600 text-sm">Be the first to share your thoughts!</p>
            </div>
          )}
        </div>
      </section>

      {/* Related Videos - Hide in landscape mobile */}
      {relatedVideos.length > 0 && (
        <section className={`py-12 px-4 ${playingInline ? 'vod-landscape-hide-content' : ''}`}>
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">More Like This</h2>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {relatedVideos.map((related) => (
                <div
                  key={related.id}
                  onClick={() => navigate(`/vod/v/${related.slug || related.id}?autoplay=true`)}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-video rounded-xl overflow-hidden bg-gray-900 mb-2">
                    {related.thumbnail_url ? (
                      <img 
                        src={related.thumbnail_url}
                        alt={related.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-purple-900/50 to-pink-900/50 flex items-center justify-center">
                        <Play className="h-8 w-8 text-white/30" />
                      </div>
                    )}
                    
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30">
                      <div className="w-10 h-10 rounded-full bg-purple-600/90 flex items-center justify-center">
                        <Play className="h-5 w-5 text-white ml-0.5" fill="white" />
                      </div>
                    </div>
                    
                    {related.tier === 'premium' && (
                      <Badge className="absolute top-2 left-2 bg-gradient-to-r from-purple-500 to-pink-500 text-xs">
                        <Crown className="h-3 w-3" />
                      </Badge>
                    )}
                  </div>
                  
                  <h3 className="font-medium text-sm line-clamp-2 group-hover:text-purple-400 transition-colors">
                    {related.title}
                  </h3>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Pricing Callout - Show for paid videos not yet purchased, or suggest Premium for ad-free */}
      {(video.price || 0) > 0 && !video.has_purchased && video.access_type !== 'purchase' && (
        <section className="py-10 px-4 bg-gradient-to-r from-purple-900/20 to-pink-900/20 border-t border-white/5">
          <div className="max-w-4xl mx-auto text-center">
            <Lock className="h-10 w-10 mx-auto mb-3 text-purple-400" />
            <h2 className="text-xl font-bold mb-2 text-white">This video requires a one-time purchase</h2>
            <p className="text-gray-400 mb-5">
              Purchase this video for {"\u00A3"}{(video.sale_price || video.price || 0).toFixed(2)} to get lifetime access
            </p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <Button
                onClick={() => setShowPaywall(true)}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
                data-testid="pricing-callout-purchase-btn"
              >
                <CreditCard className="h-4 w-4 mr-2" /> Purchase Now
              </Button>
              {!video.is_premium_user && (
                <Button
                  variant="outline"
                  onClick={() => navigate('/vod/membership')}
                  className="border-purple-500/50 text-purple-300 hover:bg-purple-500/10"
                >
                  <Crown className="h-4 w-4 mr-2" /> Go Premium for Ad-Free
                </Button>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Paywall Modal - Shows when user tries to watch a paid video */}
      {showPaywall && video && (video.price || 0) > 0 && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/85 backdrop-blur-md p-4" data-testid="paywall-modal">
          <div className="bg-gradient-to-b from-gray-900 to-gray-950 rounded-2xl w-full max-w-lg border border-white/10 shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
            {/* Thumbnail preview - smaller on mobile */}
            <div className="relative aspect-video sm:aspect-video max-h-[25vh] sm:max-h-none overflow-hidden flex-shrink-0">
              <img 
                src={video.thumbnail_url || '/placeholder.jpg'} 
                alt={video.title}
                className="w-full h-full object-cover opacity-60"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20">
                  <Lock className="h-8 w-8 text-white" />
                </div>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1 min-h-0">
              <h3 className="text-xl font-bold text-white mb-1">{video.title}</h3>
              <p className="text-gray-400 text-sm mb-5">
                {video.description?.slice(0, 100)}{video.description?.length > 100 ? '...' : ''}
              </p>
              
              {/* Price display */}
              <div className="bg-white/5 rounded-xl p-4 mb-5 border border-white/10">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">One-time purchase</p>
                    <div className="flex items-baseline gap-2">
                      {promoApplied ? (
                        <>
                          <span className="text-3xl font-bold text-white" data-testid="paywall-promo-price">
                            {promoApplied.is_free ? 'FREE' : `\u00A3${promoApplied.discounted_price.toFixed(2)}`}
                          </span>
                          <span className="text-lg text-gray-500 line-through">
                            {"\u00A3"}{(video.sale_price || video.price || 0).toFixed(2)}
                          </span>
                          <span className="text-sm font-medium text-emerald-400">{promoApplied.discount_percentage}% off</span>
                        </>
                      ) : (
                        <>
                          <span className="text-3xl font-bold text-white">
                            {"\u00A3"}{(video.sale_price || video.price || 0).toFixed(2)}
                          </span>
                          {video.sale_price && video.sale_price < video.price && (
                            <span className="text-lg text-gray-500 line-through">
                              {"\u00A3"}{video.price.toFixed(2)}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <ShoppingCart className="h-8 w-8 text-purple-400" />
                  </div>
                </div>
              </div>

              {/* Promo Code Input */}
              <div className="mb-5" data-testid="promo-code-section">
                {promoApplied ? (
                  <div className="flex items-center justify-between p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Tag className="h-4 w-4 text-emerald-400" />
                      <span className="text-sm font-medium text-emerald-400">{promoApplied.code}</span>
                      <span className="text-xs text-gray-400">
                        {promoApplied.is_free ? '— Free access!' : `— ${promoApplied.discount_percentage}% off`}
                      </span>
                    </div>
                    <button
                      onClick={handleRemovePromo}
                      className="text-gray-400 hover:text-white text-xs"
                      data-testid="promo-remove-btn"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Promo code"
                      value={promoCode}
                      onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoError(''); }}
                      className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-500"
                      data-testid="promo-code-checkout-input"
                    />
                    <button
                      onClick={handleApplyPromo}
                      disabled={promoLoading || !promoCode.trim()}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
                      data-testid="promo-apply-btn"
                    >
                      {promoLoading ? '...' : 'Apply'}
                    </button>
                  </div>
                )}
                {promoError && (
                  <p className="text-red-400 text-xs mt-1.5" data-testid="promo-error-msg">{promoError}</p>
                )}
              </div>
              
              {/* Benefits */}
              <ul className="space-y-2 mb-6 text-sm">
                <li className="flex items-center gap-2 text-gray-300">
                  <Check className="h-4 w-4 text-green-400 flex-shrink-0" />
                  Lifetime access to this video
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                  <Check className="h-4 w-4 text-green-400 flex-shrink-0" />
                  Watch anytime, on any device
                </li>
                {video.is_premium_user && (
                  <li className="flex items-center gap-2 text-gray-300">
                    <Check className="h-4 w-4 text-green-400 flex-shrink-0" />
                    Ad-free viewing (Premium member)
                  </li>
                )}
              </ul>
              
              {/* Livestream ticket holder notice */}
              {video.has_linked_livestream && !isLoggedIn && (
                <div className="mb-5 p-4 rounded-xl border border-green-500/30 bg-green-500/10" data-testid="livestream-ticket-notice">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 002 2 2 2 0 010 4 2 2 0 00-2 2v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 00-2-2 2 2 0 010-4 2 2 0 002-2V7a2 2 0 00-2-2H5z"/>
                      </svg>
                    </div>
                    <div>
                      <p className="text-green-400 font-semibold text-sm mb-1">Already purchased a livestream ticket?</p>
                      <p className="text-gray-300 text-xs leading-relaxed">
                        You get <span className="text-white font-medium">free access</span> to this VOD! Just sign in with the same account you used to purchase your livestream ticket.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Buttons */}
              <div className="space-y-3">
                {isLoggedIn ? (
                  <Button
                    onClick={() => {
                      setShowPaywall(false);
                      handlePurchase();
                    }}
                    disabled={purchasing}
                    className={`w-full ${promoApplied?.is_free ? 'bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500' : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500'} text-white font-semibold py-3 text-base`}
                    data-testid="paywall-purchase-btn"
                  >
                    {purchasing ? (
                      <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Processing...</>
                    ) : promoApplied?.is_free ? (
                      <><Check className="h-5 w-5 mr-2" /> Get Free Access</>
                    ) : promoApplied ? (
                      <><CreditCard className="h-5 w-5 mr-2" /> Purchase for {"\u00A3"}{promoApplied.discounted_price.toFixed(2)}</>
                    ) : (
                      <><CreditCard className="h-5 w-5 mr-2" /> Purchase for {"\u00A3"}{(video.sale_price || video.price || 0).toFixed(2)}</>
                    )}
                  </Button>
                ) : (
                  <>
                    {/* Inline Auth Form */}
                    <div className="border-t border-white/10 pt-4">
                      <p className="text-center text-gray-400 text-sm mb-3">Sign in or create an account to purchase</p>
                      
                      {/* Google Login */}
                      <Button
                        onClick={handlePaywallGoogleLogin}
                        className="w-full bg-white hover:bg-gray-100 text-gray-900 font-semibold py-3 text-base mb-3"
                        data-testid="paywall-google-login-btn"
                      >
                        <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                        Continue with Google
                      </Button>

                      <div className="flex items-center gap-3 mb-3">
                        <div className="flex-1 border-t border-white/10"></div>
                        <span className="text-xs text-gray-500 uppercase">or</span>
                        <div className="flex-1 border-t border-white/10"></div>
                      </div>

                      {/* Email/Password Form */}
                      <form onSubmit={handlePaywallAuth} className="space-y-2">
                        {paywallAuthMode === 'register' && (
                          <input
                            type="text"
                            placeholder="Full name"
                            value={paywallName}
                            onChange={(e) => setPaywallName(e.target.value)}
                            className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-500"
                            required
                            data-testid="paywall-name-input"
                          />
                        )}
                        <input
                          type="email"
                          placeholder="Email address"
                          value={paywallEmail}
                          onChange={(e) => setPaywallEmail(e.target.value)}
                          className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-500"
                          autoComplete="email"
                            required
                          data-testid="paywall-email-input"
                        />
                        <input
                          type="password"
                          placeholder="Password"
                          value={paywallPassword}
                          onChange={(e) => setPaywallPassword(e.target.value)}
                          className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-500"
                          autoComplete="current-password"
                            required
                          data-testid="paywall-password-input"
                        />
                        {paywallAuthError && (
                          <p className="text-red-400 text-xs" data-testid="paywall-auth-error">{paywallAuthError}</p>
                        )}
                        <Button
                          type="submit"
                          disabled={paywallAuthLoading}
                          className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-semibold py-3 text-base"
                          data-testid="paywall-auth-submit-btn"
                        >
                          {paywallAuthLoading ? (
                            <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Please wait...</>
                          ) : paywallAuthMode === 'register' ? (
                            'Create Account & Purchase'
                          ) : (
                            'Sign In & Purchase'
                          )}
                        </Button>
                      </form>

                      <p className="text-center text-sm text-gray-500 mt-3">
                        {paywallAuthMode === 'login' ? (
                          <>Don't have an account?{' '}<button onClick={() => { setPaywallAuthMode('register'); setPaywallAuthError(''); }} className="text-purple-400 hover:text-purple-300" data-testid="paywall-switch-register">Create one</button></>
                        ) : (
                          <>Already have an account?{' '}<button onClick={() => { setPaywallAuthMode('login'); setPaywallAuthError(''); }} className="text-purple-400 hover:text-purple-300" data-testid="paywall-switch-login">Sign in</button></>
                        )}
                      </p>
                    </div>
                  </>
                )}
                
                <Button
                  variant="ghost"
                  onClick={() => { setShowPaywall(false); setPaywallAuthError(''); }}
                  className="w-full text-gray-400 hover:text-white hover:bg-white/5"
                  data-testid="paywall-close-btn"
                >
                  Maybe Later
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="bg-gray-900 rounded-2xl p-6 w-full max-w-md mx-4 border border-white/10 shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Share this video</h3>
              <button
                onClick={() => {
                  setShowShareModal(false);
                  setLinkCopied(false);
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            {/* Video Preview */}
            <div className="flex items-center gap-3 bg-white/5 rounded-xl p-3 mb-6">
              <img 
                src={video.thumbnail_url} 
                alt={video.title}
                className="w-20 h-12 object-cover rounded-lg"
              />
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm truncate">{video.title}</p>
                <p className="text-gray-400 text-xs">{video.category?.name}</p>
              </div>
            </div>
            
            {/* Social Share Buttons */}
            <div className="grid grid-cols-4 gap-3 mb-6">
              {/* WhatsApp */}
              <button
                onClick={() => {
                  const url = `${API_SHARE_URL}/${video.slug || video.id}`;
                  const text = `Check out "${video.title}" on LTV VOD!`;
                  window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`, '_blank');
                }}
                className="flex flex-col items-center gap-2 p-3 bg-green-600/20 hover:bg-green-600/30 rounded-xl transition-colors group"
              >
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-6 h-6 text-white fill-current">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </div>
                <span className="text-xs text-gray-300 group-hover:text-white">WhatsApp</span>
              </button>
              
              {/* Instagram */}
              <button
                onClick={() => {
                  const url = `${API_SHARE_URL}/${video.slug || video.id}`;
                  // Instagram doesn't have direct share URL, copy link instead
                  navigator.clipboard.writeText(url);
                  alert('Link copied! Open Instagram and paste in your Story or DM.');
                }}
                className="flex flex-col items-center gap-2 p-3 bg-pink-600/20 hover:bg-pink-600/30 rounded-xl transition-colors group"
              >
                <div className="w-12 h-12 bg-gradient-to-tr from-yellow-500 via-pink-500 to-purple-500 rounded-full flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-6 h-6 text-white fill-current">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </div>
                <span className="text-xs text-gray-300 group-hover:text-white">Instagram</span>
              </button>
              
              {/* TikTok */}
              <button
                onClick={() => {
                  const url = `${API_SHARE_URL}/${video.slug || video.id}`;
                  navigator.clipboard.writeText(url);
                  alert('Link copied! Open TikTok and paste in your bio or message.');
                }}
                className="flex flex-col items-center gap-2 p-3 bg-white/10 hover:bg-white/20 rounded-xl transition-colors group"
              >
                <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center border border-white/20">
                  <svg viewBox="0 0 24 24" className="w-6 h-6 text-white fill-current">
                    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                  </svg>
                </div>
                <span className="text-xs text-gray-300 group-hover:text-white">TikTok</span>
              </button>
              
              {/* Snapchat */}
              <button
                onClick={() => {
                  const url = `${API_SHARE_URL}/${video.slug || video.id}`;
                  navigator.clipboard.writeText(url);
                  alert('Link copied! Open Snapchat and paste in your Story or Chat.');
                }}
                className="flex flex-col items-center gap-2 p-3 bg-yellow-500/20 hover:bg-yellow-500/30 rounded-xl transition-colors group"
              >
                <div className="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-6 h-6 text-black fill-current">
                    <path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.075.045.203.09.401.09.3-.016.659-.12 1.033-.301.165-.088.344-.104.464-.104.182 0 .359.029.509.09.45.149.734.479.734.838.015.449-.39.839-1.213 1.168-.089.029-.209.075-.344.119-.45.135-1.139.36-1.333.81-.09.224-.061.524.12.868l.015.015c.06.136 1.526 3.475 4.791 4.014.255.044.435.27.42.509 0 .075-.015.149-.045.225-.24.569-1.273.988-3.146 1.271-.059.091-.12.375-.164.57-.029.179-.074.36-.134.553-.076.271-.27.405-.555.405h-.03c-.135 0-.313-.031-.538-.074-.36-.075-.765-.135-1.273-.135-.3 0-.599.015-.913.074-.6.104-1.123.464-1.723.884-.853.599-1.826 1.288-3.294 1.288-.06 0-.119-.015-.18-.015h-.149c-1.468 0-2.427-.675-3.279-1.288-.599-.42-1.107-.779-1.707-.884-.314-.045-.629-.074-.928-.074-.54 0-.958.089-1.272.149-.211.043-.391.074-.54.074-.374 0-.523-.224-.583-.42-.061-.192-.09-.389-.135-.567-.046-.181-.105-.494-.166-.57-1.918-.222-2.95-.642-3.189-1.226-.031-.063-.052-.15-.055-.225-.015-.243.165-.465.42-.509 3.264-.54 4.73-3.879 4.791-4.02l.016-.029c.18-.345.224-.645.119-.869-.195-.434-.884-.658-1.332-.809-.121-.029-.24-.074-.346-.119-1.107-.435-1.257-.93-1.197-1.273.09-.479.674-.793 1.168-.793.146 0 .27.029.383.074.42.194.789.3 1.104.3.234 0 .384-.06.465-.105l-.046-.569c-.098-1.626-.225-3.651.307-4.837C7.392 1.077 10.739.807 11.727.807l.419-.015h.06z"/>
                  </svg>
                </div>
                <span className="text-xs text-gray-300 group-hover:text-white">Snapchat</span>
              </button>
            </div>
            
            {/* More Share Options */}
            <div className="grid grid-cols-4 gap-3 mb-6">
              {/* Twitter/X */}
              <button
                onClick={() => {
                  const url = `${API_SHARE_URL}/${video.slug || video.id}`;
                  const text = `Check out "${video.title}" on LTV VOD!`;
                  window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
                }}
                className="flex flex-col items-center gap-2 p-3 bg-white/10 hover:bg-white/20 rounded-xl transition-colors group"
              >
                <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center border border-white/20">
                  <svg viewBox="0 0 24 24" className="w-5 h-5 text-white fill-current">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </div>
                <span className="text-xs text-gray-300 group-hover:text-white">X</span>
              </button>
              
              {/* Facebook */}
              <button
                onClick={() => {
                  const url = `${API_SHARE_URL}/${video.slug || video.id}`;
                  window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
                }}
                className="flex flex-col items-center gap-2 p-3 bg-blue-600/20 hover:bg-blue-600/30 rounded-xl transition-colors group"
              >
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-6 h-6 text-white fill-current">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </div>
                <span className="text-xs text-gray-300 group-hover:text-white">Facebook</span>
              </button>
              
              {/* Telegram */}
              <button
                onClick={() => {
                  const url = `${API_SHARE_URL}/${video.slug || video.id}`;
                  const text = `Check out "${video.title}" on LTV VOD!`;
                  window.open(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, '_blank');
                }}
                className="flex flex-col items-center gap-2 p-3 bg-sky-500/20 hover:bg-sky-500/30 rounded-xl transition-colors group"
              >
                <div className="w-12 h-12 bg-sky-500 rounded-full flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-6 h-6 text-white fill-current">
                    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                  </svg>
                </div>
                <span className="text-xs text-gray-300 group-hover:text-white">Telegram</span>
              </button>
              
              {/* Email */}
              <button
                onClick={() => {
                  const url = `${API_SHARE_URL}/${video.slug || video.id}`;
                  const subject = `Check out: ${video.title}`;
                  const body = `I thought you might enjoy this video:\n\n${video.title}\n\n${url}`;
                  window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                }}
                className="flex flex-col items-center gap-2 p-3 bg-purple-600/20 hover:bg-purple-600/30 rounded-xl transition-colors group"
              >
                <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center">
                  <MessageCircle className="w-6 h-6 text-white" />
                </div>
                <span className="text-xs text-gray-300 group-hover:text-white">Email</span>
              </button>
            </div>
            
            {/* Copy Link Section */}
            <div className="border-t border-white/10 pt-4">
              <p className="text-sm text-gray-400 mb-3">Or copy link</p>
              <div className="flex gap-2">
                <div className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-gray-300 truncate">
                  {API_SHARE_URL}/{video.slug || video.id}
                </div>
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(`${API_SHARE_URL}/${video.slug || video.id}`);
                    setLinkCopied(true);
                    setTimeout(() => setLinkCopied(false), 2000);
                  }}
                  className={`px-4 ${linkCopied ? 'bg-green-600 hover:bg-green-600' : 'bg-purple-600 hover:bg-purple-500'}`}
                >
                  {linkCopied ? (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-2" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sign In Modal - For like/dislike */}
      {showSignInModal && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="bg-gray-900 rounded-2xl p-6 w-full max-w-sm mx-4 border border-white/10 shadow-2xl">
            <div className="text-center">
              {/* Icon */}
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-purple-600/20 flex items-center justify-center">
                <LogIn className="h-8 w-8 text-purple-400" />
              </div>
              
              {/* Title */}
              <h3 className="text-xl font-bold text-white mb-2">Sign in to react</h3>
              <p className="text-gray-400 text-sm mb-6">
                Sign in to make your opinion count
              </p>
              
              {/* Buttons */}
              <div className="space-y-3">
                <Button
                  onClick={() => {
                    setShowSignInModal(false);
                    navigate(getLoginUrl(`/vod/video/${video?.slug || video?.id || videoId}`));
                  }}
                  className="w-full bg-purple-600 hover:bg-purple-500 text-white font-semibold"
                  data-testid="signin-modal-login-btn"
                >
                  <LogIn className="h-4 w-4 mr-2" />
                  Sign In
                </Button>
                
                <Button
                  variant="outline"
                  onClick={() => setShowSignInModal(false)}
                  className="w-full border-white/20 text-gray-300 hover:bg-white/5"
                  data-testid="signin-modal-cancel-btn"
                >
                  Maybe Later
                </Button>
              </div>
              
              {/* Sign up link */}
              <p className="mt-4 text-sm text-gray-500">
                Don't have an account?{' '}
                <button
                  onClick={() => {
                    setShowSignInModal(false);
                    navigate(getSignupUrl(`/vod/video/${video?.slug || video?.id || videoId}`));
                  }}
                  className="text-purple-400 hover:text-purple-300 transition-colors"
                >
                  Sign up
                </button>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Creator Subscribe Modal - For subscribing without being logged in */}
      {showCreatorSubscribeModal && creatorProfile && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/80 p-0 sm:p-4">
          <div className="bg-gray-900 rounded-t-2xl sm:rounded-2xl p-5 sm:p-6 w-full sm:max-w-md border-t sm:border border-gray-700 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-white">Subscribe to {creatorProfile.name}</h2>
              <button onClick={() => setShowCreatorSubscribeModal(false)} className="p-1 text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-gray-300 mb-5 text-sm">
              Get notified when {creatorProfile.name} uploads new videos!
            </p>
            <input
              type="email"
              value={creatorSubscribeEmail}
              onChange={(e) => setCreatorSubscribeEmail(e.target.value)}
              placeholder="Enter your email"
              className="w-full px-4 py-3 bg-black border border-gray-600 rounded-xl focus:border-purple-500 focus:outline-none mb-4 text-white placeholder-gray-500"
              autoFocus
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowCreatorSubscribeModal(false)}
                className="flex-1 px-4 py-3 bg-gray-800 rounded-xl hover:bg-gray-700 transition text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleSubscribeToCreator}
                disabled={!creatorSubscribeEmail || subscribingToCreator}
                className="flex-1 px-4 py-3 bg-purple-600 rounded-xl hover:bg-purple-700 transition disabled:opacity-50 text-white font-medium flex items-center justify-center gap-2"
              >
                {subscribingToCreator ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Subscribing...</>
                ) : (
                  'Subscribe'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Username Modal - For commenting */}
      <Dialog open={showUsernameModal} onOpenChange={setShowUsernameModal}>
        <DialogContent className="bg-gray-900 border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              <User className="h-5 w-5 text-purple-400" />
              Set Your Username
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Choose a unique username to comment on videos. This will be your display name across LTV platforms.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Input
                value={newUsername}
                onChange={(e) => {
                  setNewUsername(e.target.value);
                  setUsernameError('');
                }}
                placeholder="Enter username (3-20 characters)"
                className="bg-white/10 border-white/20 text-white placeholder-white/50 focus:border-purple-500"
                maxLength={20}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    saveUsername();
                  }
                }}
                data-testid="vod-username-input"
                autoFocus
              />
              {usernameError && (
                <p className="text-red-400 text-sm">{usernameError}</p>
              )}
              <p className="text-gray-500 text-xs">
                Only letters and numbers allowed. Example: JohnDoe123
              </p>
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 border-white/20 text-white hover:bg-white/10"
                onClick={() => setShowUsernameModal(false)}
                disabled={savingUsername}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400"
                onClick={saveUsername}
                disabled={savingUsername || !newUsername.trim()}
                data-testid="vod-save-username-btn"
              >
                {savingUsername ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  'Save Username'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Footer */}
      <VODFooter />
    </div>
  );
};

export default VODVideoDetail;
